import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { fetchAllMembers } from "../../../Redux/Slice/AddMember";
import { fetchAllLeavesHistory } from "../../../Redux/Slice/LeaveForm";
import { fetchAllAttendanceByDateRange, fetchPayrollDropdown } from "../../../Redux/Slice/Payroll";
import Loader from "../../../Components/Loader/Loader";
import DynamicTable from "../../../Components/Table/DynamicTable";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  FiFilter,
  FiTrendingUp,
  FiUsers,
  FiUserCheck,
  FiClock,
  FiCalendar,
  FiSearch,
  FiAlertCircle,
  FiActivity,
  FiDownload,
  FiAward,
  FiPrinter,
  FiRefreshCw,
  FiUserX,
  FiX,
} from "react-icons/fi";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const BaseUrl = process.env.REACT_APP_API_URL;

const AttendanceAnalyticsDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // --- Selectors ---
  const { MemberData } = useSelector((state) => state.AddMemberSlice || {});
  const { leaveData } = useSelector((state) => state.LeaveForm || {});
  const { AllAttendanceData, payrollDropdownData, allAttendanceLoading } = useSelector(
    (state) => state.Payroll || {}
  );

  // --- Local State for Date and Filters ---
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonthVal, setSelectedMonthVal] = useState("");

  const [tempFromDate, setTempFromDate] = useState("");
  const [tempToDate, setTempToDate] = useState("");

  const [activeFromDate, setActiveFromDate] = useState("");
  const [activeToDate, setActiveToDate] = useState("");

  const [selectedDate, setSelectedDate] = useState("");

  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [manualRunning, setManualRunning] = useState(false);
  const [customAuditDate, setCustomAuditDate] = useState("");

  const fetchAuditData = async () => {
    setAuditLoading(true);
    try {
      const res = await axios.get(`${BaseUrl}/PayRoll/audit/analytics`);
      setAuditData(res.data?.data || null);
    } catch (err) {
      console.error("Error fetching audit data:", err);
    } finally {
      setAuditLoading(false);
    }
  };

  const triggerManualSnapshot = async (dateStr) => {
    setManualRunning(true);
    try {
      const res = await axios.post(`${BaseUrl}/PayRoll/audit/run`, { dateStr });
      alert(res.data?.message || "Audit snapshot executed successfully!");
      fetchAuditData();
    } catch (err) {
      console.error("Manual audit trigger error:", err);
      alert(err.response?.data?.message || "Failed to trigger audit snapshot");
    } finally {
      setManualRunning(false);
    }
  };

  const [holidays, setHolidays] = useState([]);

  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const res = await axios.get(`${BaseUrl}/Chat/holidays`);
        setHolidays(res.data?.data || []);
      } catch (err) {
        console.error("Error fetching holidays in AttendanceAnalyticsDashboard:", err);
      }
    };
    fetchHolidays();
  }, [BaseUrl]);

  const holidayMap = useMemo(() => {
    const map = new Map();
    if (Array.isArray(holidays)) {
      holidays.forEach((h) => {
        if (h.dateStr) map.set(h.dateStr, h.name || "Company Holiday");
      });
    }
    return map;
  }, [holidays]);

  const [selectedDepartment, setSelectedDepartment] = useState("All");
  const [selectedEmployee, setSelectedEmployee] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [showOnlyLate, setShowOnlyLate] = useState(false);
  const [showOnlyEarly, setShowOnlyEarly] = useState(false);
  const [lateThreshold, setLateThreshold] = useState("10:00");
  const [earlyOutThreshold, setEarlyOutThreshold] = useState("19:00");
  const [leaveTypeFilter, setLeaveTypeFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  const [selectedPerformanceDetail, setSelectedPerformanceDetail] = useState(null);

  // --- Today's Section State ---
  const [todayAttendanceData, setTodayAttendanceData] = useState([]);
  const [todayLoading, setTodayLoading] = useState(false);
  const [todaySearch, setTodaySearch] = useState("");
  const [todayDept, setTodayDept] = useState("All");
  const [todayStatus, setTodayStatus] = useState("All");
  const [todayFilterDate, setTodayFilterDate] = useState(new Date().toISOString().slice(0, 10));

  // --- Tab Selection for Monthly Section ---
  const [activeTab, setActiveTab] = useState("spreadsheet"); // "spreadsheet", "rankings", "analytics", "leaves"



  // --- Owner Leaves Inbox Fetching for Leave Diagnostics ---
  const [ownerLeaves, setOwnerLeaves] = useState([]);
  const [ownerLeavesLoading, setOwnerLeavesLoading] = useState(false);

  useEffect(() => {
    const fetchOwnerLeaves = async () => {
      const loginDataStr = Cookies.get("loginData");
      if (!loginDataStr) return;
      try {
        const loginData = JSON.parse(loginDataStr);
        const applicantUser = loginData?.data || loginData?.user || {};
        if (applicantUser?._id) {
          setOwnerLeavesLoading(true);
          const response = await axios.get(`${BaseUrl}/AdvanceLeave/PManagerRequest/${applicantUser._id}`);
          if (response.data && response.data.All) {
            setOwnerLeaves(response.data.All || []);
          }
        }
      } catch (err) {
        console.error("Failed to fetch owner leaves for diagnostics:", err);
      } finally {
        setOwnerLeavesLoading(false);
      }
    };

    fetchOwnerLeaves();
  }, [BaseUrl]);

  const handleShowPerformanceDetails = (emp, type) => {
    let records = [];
    if (type === "late") {
      records = (emp.days || []).filter((d) => d.isLate);
    } else if (type === "early") {
      records = (emp.days || []).filter((d) => d.isEarlyOut);
    } else if (type === "absent") {
      records = (emp.days || []).filter((d) => d.status === "Absent" && d.dayOfWeek !== 0);
    }

    setSelectedPerformanceDetail({
      employeeName: emp.name,
      role: emp.role,
      department: emp.department,
      type,
      records,
      monthlyStats: emp.monthlyStats,
      checkInTime: emp.checkInTime || "10:00",
      checkOutTime: emp.checkOutTime || "19:00",
    });
  };

  // Helper to format month label (e.g. "2026-07" -> "July 2026")
  const getMonthLabel = (monthStr) => {
    if (!monthStr || monthStr.length < 7) return monthStr;
    const [y, m] = monthStr.split("-");
    const date = new Date(Number(y), Number(m) - 1, 1);
    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  };

  const formatDateDMY = (dateStr) => {
    if (!dateStr || dateStr.length < 10) return "";
    const [y, m, d] = dateStr.split("-");
    return `${d}-${m}-${y}`;
  };

  const formatTodayHeaderDate = (dateStr) => {
    if (!dateStr || dateStr.length < 10) return "";
    const [y, m, d] = dateStr.split("-");
    const dateObj = new Date(Number(y), Number(m) - 1, Number(d));
    return dateObj.toLocaleDateString("en-US", { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  // --- Fetch Selected Date's attendance data ---
  const fetchTodayAttendance = async (targetDate) => {
    try {
      setTodayLoading(true);
      const dateToFetch = typeof targetDate === "string" ? targetDate : todayFilterDate;

      const response = await axios.post(`${BaseUrl}/PayRoll/AllInOutTimeDetailsfilter`, {
        from: dateToFetch,
        to: dateToFetch,
      });

      if (response.data && response.data.status === "success") {
        setTodayAttendanceData(response.data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch selected date attendance:", err);
    } finally {
      setTodayLoading(false);
    }
  };

  // Fetch target date attendance when todayFilterDate changes
  useEffect(() => {
    fetchTodayAttendance(todayFilterDate);
  }, [todayFilterDate]);

  // --- Fetch Data and Available Months on Mount ---
  useEffect(() => {
    dispatch(fetchAllMembers());
    dispatch(fetchAllLeavesHistory());
    dispatch(fetchPayrollDropdown());

    // Fetch dynamic months that have data
    const loadAvailableMonths = async () => {
      try {
        const response = await axios.get(`${BaseUrl}/PayRoll/AvailableMonths`);
        if (response.data && response.data.status === "success" && Array.isArray(response.data.data)) {
          setAvailableMonths(response.data.data);

          if (response.data.data.length > 0) {
            const latestMonth = response.data.data[0]; // e.g. "2026-07"
            const [y, m] = latestMonth.split("-");
            setSelectedYear(y);
            setSelectedMonthVal(m);

            const firstDay = `${y}-${m}-01`;
            const todayStr = new Date().toISOString().slice(0, 10);
            const lastDayNum = new Date(Number(y), Number(m), 0).getDate();
            let lastDay = `${y}-${m}-${String(lastDayNum).padStart(2, "0")}`;
            if (todayStr.startsWith(latestMonth)) {
              lastDay = todayStr;
            }

            setTempFromDate(firstDay);
            setTempToDate(lastDay);
            setActiveFromDate(firstDay);
            setActiveToDate(lastDay);
            setSelectedDate(todayStr.startsWith(latestMonth) ? todayStr : firstDay);
          } else {
            // Fallback to current month if database has absolutely no records
            const d = new Date();
            const todayStr = d.toISOString().slice(0, 10);
            const y = String(d.getFullYear());
            const m = String(d.getMonth() + 1).padStart(2, "0");
            setSelectedYear(y);
            setSelectedMonthVal(m);

            const firstDay = `${y}-${m}-01`;
            const lastDay = todayStr;
            setTempFromDate(firstDay);
            setTempToDate(lastDay);
            setActiveFromDate(firstDay);
            setActiveToDate(lastDay);
            setSelectedDate(todayStr);
          }
        }
      } catch (err) {
        console.error("Failed to load available months:", err);
      }
    };
    loadAvailableMonths();
  }, [dispatch]);

  // When activeFromDate or activeToDate changes, fetch attendance data for the full month range
  useEffect(() => {
    if (!activeFromDate || !activeToDate) return;

    const [yFrom, mFrom] = activeFromDate.split("-");
    const [yTo, mTo] = activeToDate.split("-");

    const firstDay = `${yFrom}-${mFrom}-01`;
    const lastDayNum = new Date(Number(yTo), Number(mTo), 0).getDate();
    const lastDay = `${yTo}-${mTo}-${String(lastDayNum).padStart(2, "0")}`;

    dispatch(fetchAllAttendanceByDateRange({ fromDate: firstDay, toDate: lastDay }));
  }, [activeFromDate, activeToDate, dispatch]);

  // Clamp selectedDate inside activeFromDate and activeToDate
  useEffect(() => {
    if (selectedDate < activeFromDate) {
      setSelectedDate(activeFromDate);
    } else if (selectedDate > activeToDate) {
      setSelectedDate(activeToDate);
    }
  }, [activeFromDate, activeToDate, selectedDate]);



  const handleApplyFilter = () => {
    if (tempFromDate > tempToDate) {
      alert("From Date cannot be after To Date!");
      return;
    }
    setActiveFromDate(tempFromDate);
    setActiveToDate(tempToDate);

    // Keep focused date inside new range
    if (selectedDate < tempFromDate || selectedDate > tempToDate) {
      setSelectedDate(tempFromDate);
    }
  };

  // --- Employee-to-Payroll matching logic ---
  const getPayrollId = useMemo(() => {
    return (empName) => {
      if (!empName || !Array.isArray(payrollDropdownData?.data)) return null;
      const nameLower = empName.toLowerCase().trim().replace(/\s+/g, ' ');
      let match = payrollDropdownData.data.find(
        (item) => item.MemberName.toLowerCase().trim().replace(/\s+/g, ' ') === nameLower
      );
      if (match) return match.MemberId;

      // substring match
      const parts = nameLower.split(" ").filter((p) => p.length > 0);
      if (parts.length > 0) {
        const first = parts[0];
        const subMatches = payrollDropdownData.data.filter((item) =>
          item.MemberName.toLowerCase().trim().includes(first)
        );
        if (subMatches.length === 1) return subMatches[0].MemberId;

        if (subMatches.length > 1 && parts.length > 1) {
          const last = parts[parts.length - 1];
          const doubleMatches = subMatches.filter((item) =>
            item.MemberName.toLowerCase().trim().includes(last)
          );
          if (doubleMatches.length === 1) return doubleMatches[0].MemberId;
        }
      }

      // fallback: match by first name only if it's unique
      if (parts.length > 0) {
        const first = parts[0];
        const firstNameMatches = payrollDropdownData.data.filter((item) => {
          const itemParts = item.MemberName.toLowerCase().trim().split(" ");
          return itemParts[0] === first;
        });
        if (firstNameMatches.length === 1) {
          return firstNameMatches[0].MemberId;
        }
      }

      return null;
    };
  }, [payrollDropdownData]);

  // Active employees list (including owners)
  const activeEmployees = useMemo(() => {
    if (!Array.isArray(MemberData?.data)) return [];
    const excludedNames = ["dipak rajput", "gopesh periwal", "lalit periwal", "dynamic management"];
    return MemberData.data.filter((emp) => {
      if (!emp || emp.Profile !== "On") return false;
      const role = emp.Role?.toLowerCase().trim();
      if (role === "owner") return false;
      const name = emp.Name?.toLowerCase().trim();
      if (excludedNames.includes(name)) return false;
      return true;
    });
  }, [MemberData]);

  // List of owner names for filtering out raw leaves and attendance
  const ownerNames = useMemo(() => {
    if (!Array.isArray(MemberData?.data)) return [];
    return MemberData.data
      .filter((emp) => emp && emp.Role?.toLowerCase() === "owner")
      .map((emp) => emp.Name?.toLowerCase().trim());
  }, [MemberData]);



  // List of all available Departments derived from unique employee Roles
  const departments = useMemo(() => {
    const roles = activeEmployees.map((emp) => {
      if (!emp.Role) return "General";
      const parts = emp.Role.split(" ");
      return parts[0] || "General";
    });
    return ["All", ...new Set(roles)];
  }, [activeEmployees]);

  // Helper function to extract time part from ISO string
  const getLocalTime = (dateStr) => {
    if (!dateStr || dateStr.length < 16) return "";
    return dateStr.slice(11, 16);
  };

  // Helper function to compute duration in minutes
  const getDiffMinutes = (time1, time2) => {
    if (!time1 || !time2) return 0;
    const [h1, m1] = time1.split(":").map(Number);
    const [h2, m2] = time2.split(":").map(Number);
    return (h1 * 60 + m1) - (h2 * 60 + m2);
  };

  // Helper to format minutes into readable HH:MM duration
  const formatMinutesToHHMM = (totalMins) => {
    if (!totalMins || isNaN(totalMins) || totalMins < 0) return "00h 00m";
    const hrs = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    return `${String(hrs).padStart(2, "0")}h ${String(mins).padStart(2, "0")}m`;
  };

  // Helper to format deduction time
  const formatDeductionTime = (mins) => {
    if (!mins || isNaN(mins) || mins <= 0) return "-";
    const hrs = Math.floor(mins / 60);
    const m = mins % 60;
    if (hrs > 0) {
      return `${hrs}h ${m}m`;
    }
    return `${m}m`;
  };

  // --- Core Calculations for Workforce ---
  const attendanceAnalytics = useMemo(() => {
    if (!activeFromDate || !activeToDate || !selectedDate) return [];
    const parsedPunches = Array.isArray(AllAttendanceData?.data) ? AllAttendanceData.data : [];
    const parsedLeaves = Array.isArray(leaveData) ? leaveData : Array.isArray(leaveData?.data) ? leaveData.data : [];
    console.log("DEBUG CALCULATIONS:", {
      activeFromDate,
      activeToDate,
      selectedDate,
      parsedPunchesLength: parsedPunches.length,
      AllAttendanceDataStateExists: !!AllAttendanceData,
      AllAttendanceDataHasDataArray: Array.isArray(AllAttendanceData?.data)
    });

    // Group punches by Employee and Date
    const empPunchesByDate = {};
    parsedPunches.forEach((p) => {
      if (!p || !p.MemberID) return;
      const dateKey = p.InDate
        ? p.InDate.slice(0, 10)
        : p.OutDate
          ? p.OutDate.slice(0, 10)
          : null;
      if (!dateKey) return;

      if (!empPunchesByDate[p.MemberID]) {
        empPunchesByDate[p.MemberID] = {};
      }
      if (!empPunchesByDate[p.MemberID][dateKey]) {
        empPunchesByDate[p.MemberID][dateKey] = [];
      }
      empPunchesByDate[p.MemberID][dateKey].push(p);
    });

    // Build Daily metrics maps
    return activeEmployees.map((emp) => {
      const payrollId = getPayrollId(emp.Name);
      const punches = payrollId ? empPunchesByDate[payrollId] || {} : {};
      const dept = emp.Role ? emp.Role.split(" ")[0] || "General" : "General";
      const empCheckIn = emp.CheckInTime || "10:00";
      const empCheckOut = emp.CheckOutTime || "19:00";

      // Compute statistics for all days in the date range [activeFromDate, activeToDate]
      const currentRangeDays = [];
      const start = new Date(activeFromDate);
      const end = new Date(activeToDate);

      let presentDaysCount = 0;
      let absentDaysCount = 0;
      let leaveDaysCount = 0;
      let halfDaysCount = 0;
      let lateArrivalsCount = 0;
      let earlyOutsCount = 0;
      let totalLateMinutes = 0;
      let totalEarlyOutMinutes = 0;
      let totalWorkingMinutes = 0;
      let lastLateArrivalDate = null;

      // Safe date loop iteration
      const tempDate = new Date(start);
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

      const getJoiningDateString = (dateInput) => {
        if (!dateInput) return "";
        try {
          const d = new Date(dateInput);
          if (isNaN(d.getTime())) return "";
          return d.toISOString().slice(0, 10);
        } catch (e) {
          return "";
        }
      };
      const joinLimit = getJoiningDateString(emp.JoiningDate);

      while (tempDate <= end) {
        const year = tempDate.getFullYear();
        const month = tempDate.getMonth();
        const day = tempDate.getDate();
        const dayStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

        // Stop counting days that are in the future relative to today
        if (activeFromDate !== activeToDate && dayStr > todayStr) {
          break;
        }

        // Check if employee has joined yet on this date
        if (joinLimit && dayStr < joinLimit) {
          tempDate.setDate(tempDate.getDate() + 1);
          continue;
        }

        const dayOfWeek = tempDate.getDay(); // 0 = Sunday

        // Check approved leave
        const dayLeave = parsedLeaves.find(
          (lv) =>
            lv.applicantName?.toLowerCase() === emp.Name?.toLowerCase() &&
            lv.Status === "Approved" &&
            lv.fromDate <= dayStr &&
            dayStr <= lv.toDate
        );

        const dayPunches = punches[dayStr] || [];
        let firstIn = null;
        let lastOut = null;

        dayPunches.forEach((p) => {
          if (p.InDate) {
            if (!firstIn || p.InDate < firstIn) firstIn = p.InDate;
          }
          if (p.OutDate) {
            if (!lastOut || p.OutDate > lastOut) lastOut = p.OutDate;
          }
        });

        const localInTime = firstIn ? getLocalTime(firstIn) : "";
        const localOutTime = lastOut ? getLocalTime(lastOut) : "";

        let status = "Absent";
        let isLate = false;
        let isEarlyOut = false;
        let workMinutes = 0;

        const hasPunches = dayPunches.length > 0;

        // Status Classification
        if (hasPunches) {
          if (dayLeave && dayLeave.LeaveType === "Half Day") {
            status = "Half Day";
            halfDaysCount++;
          } else {
            status = "Present";
            presentDaysCount++;
          }
        } else {
          if (dayLeave) {
            if (dayLeave.LeaveType === "Half Day") {
              status = "Half Day";
              halfDaysCount++;
            } else if (["Late Arrival", "Early Leave", "Time Break"].includes(dayLeave.LeaveType)) {
              if (dayOfWeek === 0) {
                status = "Weekend Off";
              } else if (holidayMap.has(dayStr)) {
                status = "Holiday";
              } else {
                status = "Absent";
                absentDaysCount++;
              }
            } else {
              status = "Leave";
              leaveDaysCount++;
            }
          } else {
            if (dayOfWeek === 0) {
              status = "Weekend Off";
            } else if (holidayMap.has(dayStr)) {
              status = "Holiday";
            } else {
              status = "Absent";
              absentDaysCount++;
            }
          }
        }

        if (hasPunches) {
          // check late arrival
          if (localInTime) {
            const lateDiff = getDiffMinutes(localInTime, empCheckIn);
            if (lateDiff > 0) {
              isLate = true;
              lateArrivalsCount++;
              lastLateArrivalDate = dayStr;
              totalLateMinutes += lateDiff;
            }
          }

          // check early out
          if (localOutTime) {
            let shouldCalculateEarlyOut = true;
            if (dayStr === todayStr) {
              const now = new Date();
              const currentHour = now.getHours();
              const currentMin = now.getMinutes();
              const currentTimeStr = `${String(currentHour).padStart(2, "0")}:${String(currentMin).padStart(2, "0")}`;
              if (currentTimeStr < empCheckOut) {
                shouldCalculateEarlyOut = false;
              }
            }
            if (shouldCalculateEarlyOut) {
              const earlyDiff = getDiffMinutes(empCheckOut, localOutTime);
              if (earlyDiff > 0) {
                isEarlyOut = true;
                earlyOutsCount++;
                totalEarlyOutMinutes += earlyDiff;
              }
            }
          }

          // working duration
          if (firstIn && lastOut) {
            const diffMs = new Date(lastOut.slice(0, 19)) - new Date(firstIn.slice(0, 19));
            workMinutes = Math.max(0, Math.floor(diffMs / (60 * 1000)));
            totalWorkingMinutes += workMinutes;
          }
        }

        currentRangeDays.push({
          date: dayStr,
          dayOfWeek,
          status,
          isLate,
          isEarlyOut,
          workMinutes,
          inTime: localInTime,
          outTime: localOutTime,
          leaveType: dayLeave ? dayLeave.LeaveType : null,
          leaveReason: dayLeave ? (dayLeave.Reason || dayLeave.leaveReason || "") : null,
        });

        // Increment day by 1
        tempDate.setDate(tempDate.getDate() + 1);
      }

      // Selected Day specifics (focused day within custom range)
      const selectedDayObj = currentRangeDays.find((d) => d.date === selectedDate) || {
        date: selectedDate,
        status: "Absent",
        isLate: false,
        isEarlyOut: false,
        workMinutes: 0,
        inTime: "",
        outTime: "",
        leaveType: null,
        leaveReason: null,
      };

      // Compute consecutive absent days ending on selectedDate
      let consecutiveAbsentDays = 0;
      if (selectedDayObj.status === "Absent") {
        const activeDateObj = new Date(selectedDate);
        let checkDate = new Date(activeDateObj);

        while (true) {
          const year = checkDate.getFullYear();
          const month = checkDate.getMonth();
          const day = checkDate.getDate();
          const dayStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

          // Look back up to the first day of the current month
          const [yVal, mVal] = selectedDate.split("-");
          const firstDayOfMonth = `${yVal}-${mVal}-01`;
          if (dayStr < firstDayOfMonth) {
            break;
          }

          const dayOfWeek = checkDate.getDay(); // 0 = Sunday
          if (dayOfWeek === 0) {
            // Sunday is weekly off, skip it but do not break the streak
            checkDate.setDate(checkDate.getDate() - 1);
            continue;
          }

          const dayPunches = punches[dayStr] || [];
          const hasPunches = dayPunches.length > 0;
          const dayLeave = parsedLeaves.find(
            (lv) =>
              lv.applicantName?.toLowerCase() === emp.Name?.toLowerCase() &&
              lv.Status === "Approved" &&
              lv.fromDate <= dayStr &&
              dayStr <= lv.toDate
          );

          if (!hasPunches && !dayLeave) {
            consecutiveAbsentDays++;
          } else {
            break;
          }

          // Go back 1 day
          checkDate.setDate(checkDate.getDate() - 1);

          // Break if we look back more than 30 days
          const diffTime = Math.abs(activeDateObj - checkDate);
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          if (diffDays > 30) {
            break;
          }
        }
      }

      // Attendance percentage = (Present + 0.5 * Half Day) / (Working days in range)
      const sundays = currentRangeDays.filter((d) => d.dayOfWeek === 0).length;
      const totalWorkdays = currentRangeDays.length - sundays;
      const attendancePercent = totalWorkdays > 0
        ? Math.round(((presentDaysCount + halfDaysCount * 0.5) / totalWorkdays) * 100)
        : 0;

      return {
        id: emp._id,
        name: emp.Name,
        profilePhoto: emp.ProfilePhoto,
        role: emp.Role || "General Staff",
        department: dept,
        payrollId,
        joiningDate: emp.JoiningDate,
        selectedDay: selectedDayObj,
        consecutiveAbsentDays,
        days: currentRangeDays,
        checkInTime: empCheckIn,
        checkOutTime: empCheckOut,
        monthlyStats: {
          presentDays: presentDaysCount,
          absentDays: absentDaysCount,
          leaveDays: leaveDaysCount,
          halfDays: halfDaysCount,
          lateCount: lateArrivalsCount,
          earlyCount: earlyOutsCount,
          lateMinutes: totalLateMinutes,
          earlyOutMinutes: totalEarlyOutMinutes,
          attendancePercentage: attendancePercent,
          workingMinutes: totalWorkingMinutes,
          lastLateDate: lastLateArrivalDate,
        },
      };
    })
      .filter((emp) => {
        // Exclude employees who have not joined yet (zero expected days generated in this date range)
        return emp.days && emp.days.length > 0;
      })
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [activeEmployees, AllAttendanceData, leaveData, selectedDate, activeFromDate, activeToDate, getPayrollId, lateThreshold, earlyOutThreshold]);

  // --- Today's Attendance Calculations (Always evaluated for today's calendar date) ---
  const todayStr = useMemo(() => {
    return todayFilterDate;
  }, [todayFilterDate]);

  const todayAnalytics = useMemo(() => {
    if (!todayAttendanceData) return [];

    const todayPunchesByEmp = {};
    const parsedPunches = Array.isArray(todayAttendanceData) ? todayAttendanceData : [];

    parsedPunches.forEach((p) => {
      if (!p || !p.MemberID) return;
      if (!todayPunchesByEmp[p.MemberID]) {
        todayPunchesByEmp[p.MemberID] = [];
      }
      todayPunchesByEmp[p.MemberID].push(p);
    });

    const parsedLeaves = Array.isArray(leaveData?.data) ? leaveData.data : [];

    return activeEmployees.map((emp) => {
      const payrollId = getPayrollId(emp.Name);
      const dayPunches = payrollId ? todayPunchesByEmp[payrollId] || [] : [];
      const dept = emp.Role ? emp.Role.split(" ")[0] || "General" : "General";
      const empCheckIn = emp.CheckInTime || "10:00";
      const empCheckOut = emp.CheckOutTime || "19:00";

      const checkDate = new Date(todayStr);
      const dayOfWeek = checkDate.getDay();

      const dayLeave = parsedLeaves.find(
        (lv) =>
          lv.applicantName?.toLowerCase() === emp.Name?.toLowerCase() &&
          lv.Status === "Approved" &&
          lv.fromDate <= todayStr &&
          todayStr <= lv.toDate
      );

      let firstIn = null;
      let lastOut = null;
      dayPunches.forEach((p) => {
        if (p.InDate) {
          if (!firstIn || p.InDate < firstIn) firstIn = p.InDate;
        }
        if (p.OutDate) {
          if (!lastOut || p.OutDate > lastOut) lastOut = p.OutDate;
        }
      });

      const localInTime = firstIn ? getLocalTime(firstIn) : "";
      const localOutTime = lastOut ? getLocalTime(lastOut) : "";

      let status = "Absent";
      let isLate = false;
      let isEarlyOut = false;
      let workMinutes = 0;
      const hasPunches = dayPunches.length > 0;

      if (hasPunches) {
        if (dayLeave && dayLeave.LeaveType === "Half Day") {
          status = "Half Day";
        } else if (dayLeave && (dayLeave.LeaveType === "Work From Home" || dayLeave.LeaveType === "WFH")) {
          status = "Work From Home";
        } else {
          status = "Present";
        }
      } else {
        if (dayLeave) {
          if (dayLeave.LeaveType === "Work From Home" || dayLeave.LeaveType === "WFH") {
            status = "Work From Home";
          } else if (dayLeave.LeaveType === "Half Day") {
            status = "Half Day";
          } else if (["Late Arrival", "Early Leave", "Time Break"].includes(dayLeave.LeaveType)) {
            status = dayOfWeek === 0 ? "Weekend Off" : (holidayMap.has(selectedDate) ? "Holiday" : "Absent");
          } else {
            status = "Leave";
          }
        } else {
          status = dayOfWeek === 0 ? "Weekend Off" : (holidayMap.has(selectedDate) ? "Holiday" : "Absent");
        }
      }

      if (hasPunches) {
        if (localInTime) {
          const lateDiff = getDiffMinutes(localInTime, empCheckIn);
          if (lateDiff > 0) {
            isLate = true;
          }
        }

        if (localOutTime) {
          let shouldCalculateEarlyOut = true;
          const now = new Date();
          const currentHour = now.getHours();
          const currentMin = now.getMinutes();
          const currentTimeStr = `${String(currentHour).padStart(2, "0")}:${String(currentMin).padStart(2, "0")}`;
          if (currentTimeStr < empCheckOut) {
            shouldCalculateEarlyOut = false;
          }
          if (shouldCalculateEarlyOut) {
            const earlyDiff = getDiffMinutes(empCheckOut, localOutTime);
            if (earlyDiff > 0) {
              isEarlyOut = true;
            }
          }
        }

        if (firstIn && lastOut) {
          const diffMs = new Date(lastOut.slice(0, 19)) - new Date(firstIn.slice(0, 19));
          workMinutes = Math.max(0, Math.floor(diffMs / (60 * 1000)));
        }
      }

      return {
        id: emp._id,
        name: emp.Name,
        profilePhoto: emp.ProfilePhoto,
        role: emp.Role || "General Staff",
        department: dept,
        inTime: localInTime,
        outTime: localOutTime,
        status,
        isLate,
        isEarlyOut,
        workMinutes,
        dayOfWeek,
        checkInTime: empCheckIn,
        checkOutTime: empCheckOut,
        leaveType: dayLeave ? dayLeave.LeaveType : null,
        leaveReason: dayLeave ? (dayLeave.Reason || dayLeave.leaveReason || "") : null,
      };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [activeEmployees, todayAttendanceData, leaveData, todayStr, getPayrollId, lateThreshold, earlyOutThreshold]);

  const todayKpis = useMemo(() => {
    const total = todayAnalytics.length;
    let present = 0;
    let absent = 0;
    let onLeave = 0;
    let wfh = 0;
    let halfDay = 0;
    let late = 0;

    todayAnalytics.forEach((emp) => {
      if (emp.status === "Present") present++;
      else if (emp.status === "Absent") {
        if (emp.dayOfWeek !== 0) absent++;
      }
      else if (emp.status === "Leave") onLeave++;
      else if (emp.status === "Work From Home") wfh++;
      else if (emp.status === "Half Day") halfDay++;

      if (emp.isLate) late++;
    });

    return {
      total,
      present,
      absent,
      onLeave,
      wfh,
      halfDay,
      late,
    };
  }, [todayAnalytics]);

  const filteredTodayData = useMemo(() => {
    return todayAnalytics.filter((emp) => {
      if (todayDept !== "All" && emp.department !== todayDept) return false;
      if (todayStatus !== "All") {
        if (todayStatus === "Late") {
          if (!emp.isLate) return false;
        } else if (emp.status !== todayStatus) {
          return false;
        }
      }
      if (
        todaySearch &&
        !emp.name.toLowerCase().includes(todaySearch.toLowerCase()) &&
        !emp.role.toLowerCase().includes(todaySearch.toLowerCase())
      ) {
        return false;
      }
      return true;
    });
  }, [todayAnalytics, todayDept, todayStatus, todaySearch]);

  // --- Filtering Calculations ---
  const filteredDashboardData = useMemo(() => {
    return attendanceAnalytics.filter((emp) => {
      // 1. Department Filter
      if (selectedDepartment !== "All" && emp.department !== selectedDepartment) return false;

      // 2. Status Filter
      if (selectedStatus !== "All") {
        if (selectedStatus === "Present" && emp.monthlyStats.presentDays === 0) return false;
        if (selectedStatus === "Absent" && emp.monthlyStats.absentDays === 0) return false;
        if (selectedStatus === "Late" && emp.monthlyStats.lateCount === 0) return false;
        if (selectedStatus === "Early" && emp.monthlyStats.earlyCount === 0) return false;
        if (selectedStatus === "Leave" && emp.monthlyStats.leaveDays === 0) return false;
        if (selectedStatus === "Half Day" && emp.monthlyStats.halfDays === 0) return false;
      }

      // 3. Search query
      if (
        searchQuery &&
        !emp.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !emp.role.toLowerCase().includes(searchQuery.toLowerCase())
      ) {
        return false;
      }

      return true;
    });
  }, [attendanceAnalytics, selectedDepartment, selectedStatus, searchQuery]);

  // --- Table Column Definitions for Today's Dashboard Table ---
  const todayColumns = useMemo(() => {
    return [
      {
        field: "employee",
        headerName: "Employee",
        flex: 1.5,
        renderCell: (params) => {
          const emp = params.row;
          return (
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full overflow-hidden bg-slate-200 flex-shrink-0 border border-slate-200/60 shadow-sm">
                {emp.profilePhoto ? (
                  <img
                    src={emp.profilePhoto}
                    alt={emp.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="h-full w-full flex items-center justify-center font-bold text-indigo-700 bg-indigo-50/80">
                    {emp.name.charAt(0)}
                  </div>
                )}
              </div>
              <span className="font-bold text-slate-750 text-sm">{emp.name}</span>
            </div>
          );
        }
      },
      {
        field: "deptRole",
        headerName: "Dept & Role",
        flex: 1.2,
        renderCell: (params) => {
          const emp = params.row;
          return (
            <div className="text-slate-500">
              <span className="block font-bold text-slate-750 text-sm">{emp.department}</span>
              <span className="block text-[11px] text-slate-400 mt-0.5 font-medium">{emp.role}</span>
            </div>
          );
        }
      },
      {
        field: "inTime",
        headerName: "In Time",
        flex: 1.0,
        renderCell: (params) => {
          const emp = params.row;
          return (
            <div className="flex items-center">
              <span className="font-semibold text-slate-700 font-mono text-sm">
                {emp.inTime || "--:--"}
              </span>
              {emp.isLate && (
                <span className="ml-1.5 text-[10px] text-rose-600 bg-rose-50 font-extrabold px-1.5 py-0.5 rounded border border-rose-100 animate-pulse">
                  Late
                </span>
              )}
            </div>
          );
        }
      },
      {
        field: "outTime",
        headerName: "Last Out Time",
        flex: 1.0,
        renderCell: (params) => {
          const emp = params.row;
          return (
            <span className="font-semibold text-slate-700 font-mono text-sm">
              {emp.outTime || "--:--"}
            </span>
          );
        }
      },
      {
        field: "status",
        headerName: "Status",
        flex: 1.2,
        renderCell: (params) => {
          const emp = params.row;
          return (
            <div>
              <span
                className={`inline-block px-2.5 py-1 text-xs font-bold rounded-full ${emp.status === "Present"
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                  : emp.status === "Leave"
                    ? "bg-indigo-50 text-indigo-700 border border-indigo-100"
                    : emp.status === "Half Day"
                      ? "bg-amber-50 text-amber-700 border-amber-100"
                      : emp.status === "Work From Home"
                        ? "bg-sky-50 text-sky-700 border border-sky-100"
                        : "bg-rose-50 text-rose-700 border border-rose-100"
                  }`}
              >
                {emp.status === "Leave" && emp.leaveType
                  ? `Leave (${emp.leaveType})`
                  : emp.status === "Half Day" && emp.leaveType
                    ? `Half Day (${emp.leaveType})`
                    : emp.status === "Present" && emp.leaveType
                      ? `Present (${emp.leaveType})`
                      : emp.status === "Absent" && emp.leaveType
                        ? `Absent (${emp.leaveType})`
                        : emp.status}
              </span>
              {((emp.status === "Leave" || emp.status === "Half Day" || emp.status === "Present" || emp.status === "Absent") && emp.leaveReason) && (
                <span className="block text-[11px] text-slate-400 mt-1 italic max-w-[120px] truncate" title={emp.leaveReason}>
                  {emp.leaveReason}
                </span>
              )}
            </div>
          );
        }
      },
      {
        field: "workHours",
        headerName: "Work Hours",
        flex: 1.0,
        renderCell: (params) => {
          const emp = params.row;
          return (
            <div className="font-bold font-mono text-slate-650 text-sm">
              {formatMinutesToHHMM(emp.workMinutes)}
            </div>
          );
        }
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 1.4,
        renderCell: (params) => {
          const emp = params.row;
          const matchedEmp = attendanceAnalytics.find(e => e.name === emp.name);
          return (
            <div className="flex gap-2">
              <button
                onClick={() => {
                  if (matchedEmp) {
                    handleShowPerformanceDetails(matchedEmp, "late");
                  }
                }}
                className="text-xs text-indigo-605 hover:text-indigo-800 font-bold bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1.5 rounded-lg border border-indigo-100 transition-all cursor-pointer whitespace-nowrap"
              >
                Analyze Logs
              </button>
              <button
                onClick={() => {
                  const payrollId = getPayrollId(emp.name);
                  if (payrollId) {
                    navigate("/Payroll", { state: { memberId: payrollId } });
                  } else {
                    alert("Payroll ID not found for " + emp.name);
                  }
                }}
                className="text-xs text-emerald-650 hover:text-emerald-800 font-bold bg-emerald-50 hover:bg-emerald-100 px-2.5 py-1.5 rounded-lg border border-emerald-100 transition-all cursor-pointer whitespace-nowrap"
              >
                Payroll
              </button>
            </div>
          );
        }
      }
    ];
  }, [attendanceAnalytics, getPayrollId, navigate]);

  // --- Table Column Definitions for DynamicTable component ---


  const statisticsColumns = useMemo(() => {
    return [
      {
        field: "employeeName",
        headerName: "Employee Name",
        flex: 1.5,
        renderCell: (params) => {
          return (
            <span className="font-bold text-slate-750 text-sm">
              {params.row.name}
            </span>
          );
        }
      },
      {
        field: "present",
        headerName: "Present",
        flex: 1.0,
        renderCell: (params) => {
          return (
            <div className="font-extrabold text-slate-700 font-mono text-sm">
              {params.row.monthlyStats.presentDays}
            </div>
          );
        }
      },
      {
        field: "absent",
        headerName: "Absent",
        flex: 1.0,
        renderCell: (params) => {
          return (
            <div className="font-black text-rose-600 font-mono text-sm">
              {params.row.monthlyStats.absentDays}
            </div>
          );
        }
      },
      {
        field: "leave",
        headerName: "Leave",
        flex: 1.0,
        renderCell: (params) => {
          return (
            <div className="font-extrabold text-indigo-600 font-mono text-sm">
              {params.row.monthlyStats.leaveDays}
            </div>
          );
        }
      },
      {
        field: "halfDay",
        headerName: "Half Day",
        flex: 1.0,
        renderCell: (params) => {
          return (
            <div className="font-extrabold text-amber-600 font-mono text-sm">
              {params.row.monthlyStats.halfDays}
            </div>
          );
        }
      },
      {
        field: "lates",
        headerName: "Lates",
        flex: 1.0,
        renderCell: (params) => {
          return (
            <div className="font-black text-amber-700 font-mono text-sm">
              {params.row.monthlyStats.lateCount}
            </div>
          );
        }
      },
      {
        field: "lateDuration",
        headerName: "Late Duration",
        flex: 1.2,
        renderCell: (params) => {
          return (
            <span className="text-rose-650 font-black font-mono text-xs">
              {formatDeductionTime(params.row.monthlyStats.lateMinutes)}
            </span>
          );
        }
      },
      {
        field: "earlies",
        headerName: "Earlies",
        flex: 1.0,
        renderCell: (params) => {
          return (
            <div className="font-extrabold text-slate-600 font-mono text-sm">
              {params.row.monthlyStats.earlyCount}
            </div>
          );
        }
      },
      {
        field: "earlyDuration",
        headerName: "Early Out Duration",
        flex: 1.2,
        renderCell: (params) => {
          return (
            <span className="text-rose-650 font-black font-mono text-xs">
              {formatDeductionTime(params.row.monthlyStats.earlyOutMinutes)}
            </span>
          );
        }
      },
      {
        field: "totalDeduction",
        headerName: "Total Deduction",
        flex: 1.2,
        renderCell: (params) => {
          const totalDeductionMinutes = params.row.monthlyStats.lateMinutes + params.row.monthlyStats.earlyOutMinutes;
          return (
            <span className="text-red-650 font-black font-mono text-xs">
              {formatDeductionTime(totalDeductionMinutes)}
            </span>
          );
        }
      },
      {
        field: "hoursWorked",
        headerName: "Hours Worked",
        flex: 1.2,
        renderCell: (params) => {
          return (
            <div className="font-mono font-black text-slate-750 text-sm">
              {Math.round(params.row.monthlyStats.workingMinutes / 60)} hrs
            </div>
          );
        }
      }
    ];
  }, []);

  // --- KPIs Calculations ---
  const kpis = useMemo(() => {
    const total = activeEmployees.length;

    let present = 0;
    let absent = 0;
    let onLeave = 0;
    let late = 0;
    let earlyOut = 0;
    let halfDay = 0;

    attendanceAnalytics.forEach((emp) => {
      const status = emp.selectedDay.status;
      if (status === "Present") present++;
      else if (status === "Absent") absent++;
      else if (status === "Leave") onLeave++;
      else if (status === "Half Day") halfDay++;

      if (emp.selectedDay.isLate) late++;
      if (emp.selectedDay.isEarlyOut) earlyOut++;
    });

    const presentPct = total > 0 ? Math.round(((present + halfDay * 0.5) / total) * 100) : 0;
    const absentPct = total > 0 ? Math.round((absent / total) * 100) : 0;
    const latePct = total > 0 ? Math.round((late / total) * 100) : 0;
    const earlyPct = total > 0 ? Math.round((earlyOut / total) * 100) : 0;

    const attendancePercent = presentPct;
    const leavePercent = total > 0 ? Math.round(((onLeave + halfDay * 0.5) / total) * 100) : 0;

    // Workforce Health score
    const healthScore = total > 0 ? Math.round(((present + halfDay * 0.5) / (total - onLeave)) * 100) : 100;

    return {
      total,
      present,
      absent,
      onLeave,
      late,
      earlyOut,
      halfDay,
      presentPct,
      absentPct,
      latePct,
      earlyPct,
      attendancePercent,
      leavePercent,
      healthScore: isNaN(healthScore) ? 100 : Math.min(100, healthScore),
    };
  }, [attendanceAnalytics, activeEmployees]);

  // --- Monthly Cumulative KPIs Calculations ---
  const monthlyKPIs = useMemo(() => {
    let totalLates = 0;
    let totalLateMinutes = 0;
    let totalEarlies = 0;
    let totalEarlyMinutes = 0;
    let sumPercentage = 0;
    let countEmployees = 0;

    attendanceAnalytics.forEach((emp) => {
      totalLates += emp.monthlyStats?.lateCount || 0;
      totalLateMinutes += emp.monthlyStats?.lateMinutes || 0;
      totalEarlies += emp.monthlyStats?.earlyCount || 0;
      totalEarlyMinutes += emp.monthlyStats?.earlyOutMinutes || 0;
      sumPercentage += emp.monthlyStats?.attendancePercentage || 0;
      countEmployees++;
    });

    const avgAttendancePercent = countEmployees > 0
      ? Math.round(sumPercentage / countEmployees)
      : 0;

    return {
      attendancePercent: avgAttendancePercent,
      late: totalLates,
      lateMinutes: totalLateMinutes,
      early: totalEarlies,
      earlyMinutes: totalEarlyMinutes,
    };
  }, [attendanceAnalytics]);

  // --- Leave Diagnostics Helpers ---
  const pendingApprovals = useMemo(() => {
    const list = ownerLeaves.length > 0 ? ownerLeaves : (Array.isArray(leaveData) ? leaveData : leaveData?.data || []);
    return list.filter((lv) => lv.Status === "Pending");
  }, [ownerLeaves, leaveData]);

  const upcomingLeaves = useMemo(() => {
    const list = Array.isArray(leaveData) ? leaveData : leaveData?.data || [];
    return list.filter((lv) => lv.Status === "Approved" && lv.fromDate > todayStr);
  }, [leaveData, todayStr]);

  // --- Employee Performance Rankings ---
  const rankings = useMemo(() => {
    const list = attendanceAnalytics.filter((emp) => {
      const nameLower = emp.name?.toLowerCase().trim();
      return nameLower !== "gopesh periwal" && nameLower !== "dipak rajput";
    });

    return {
      mostLate: [...list]
        .filter((emp) => emp.monthlyStats.lateCount > 0)
        .sort((a, b) => b.monthlyStats.lateCount - a.monthlyStats.lateCount),

      mostEarlyOuts: [...list]
        .filter((emp) => emp.monthlyStats.earlyCount > 0)
        .sort((a, b) => b.monthlyStats.earlyCount - a.monthlyStats.earlyCount),

      mostAbsents: [...list]
        .filter((emp) => emp.monthlyStats.absentDays > 0)
        .sort((a, b) => b.monthlyStats.absentDays - a.monthlyStats.absentDays),
    };
  }, [attendanceAnalytics]);

  // --- Visual Charts Data Models ---
  const chartsData = useMemo(() => {
    // 1. Status Pie
    let totalPresent = 0;
    let totalAbsent = 0;
    let totalLeave = 0;
    let totalHalfDay = 0;

    attendanceAnalytics.forEach((emp) => {
      totalPresent += emp.monthlyStats?.presentDays || 0;
      totalAbsent += emp.monthlyStats?.absentDays || 0;
      totalLeave += emp.monthlyStats?.leaveDays || 0;
      totalHalfDay += emp.monthlyStats?.halfDays || 0;
    });

    const pieData = [
      { name: "Present", value: totalPresent, color: "#10B981" },
      { name: "Absent", value: totalAbsent, color: "#EF4444" },
      { name: "On Leave", value: totalLeave, color: "#6366F1" },
      { name: "Half Day", value: totalHalfDay, color: "#F59E0B" },
    ];

    // 2. Daily Trends (Always calculated for the full selected calendar month)
    const trends = [];
    const y = selectedYear;
    const m = selectedMonthVal;

    if (y && m) {
      const lastDayNum = new Date(Number(y), Number(m), 0).getDate();

      // Group punches in AllAttendanceData.data by date and employee for efficient lookup
      // USING MemberID and dateKey, exactly as done in core calculations
      const empPunchesByDate = {};
      const parsedPunches = Array.isArray(AllAttendanceData?.data) ? AllAttendanceData.data : [];

      parsedPunches.forEach((p) => {
        if (!p || !p.MemberID) return;
        const dateKey = p.InDate
          ? p.InDate.slice(0, 10)
          : p.OutDate
            ? p.OutDate.slice(0, 10)
            : null;
        if (!dateKey) return;

        if (!empPunchesByDate[p.MemberID]) {
          empPunchesByDate[p.MemberID] = {};
        }
        if (!empPunchesByDate[p.MemberID][dateKey]) {
          empPunchesByDate[p.MemberID][dateKey] = [];
        }
        empPunchesByDate[p.MemberID][dateKey].push(p);
      });

      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

      for (let dVal = 1; dVal <= lastDayNum; dVal++) {
        const dayStr = `${y}-${m}-${String(dVal).padStart(2, "0")}`;

        // Stop showing trend in the future relative to today if it's the current month
        if (dayStr > todayStr) {
          break;
        }

        let presentCount = 0;
        let lateCount = 0;

        activeEmployees.forEach((emp) => {
          // Check if employee has joined yet on this date
          const getJoiningDateString = (dateInput) => {
            if (!dateInput) return "";
            try {
              const d = new Date(dateInput);
              if (isNaN(d.getTime())) return "";
              return d.toISOString().slice(0, 10);
            } catch (e) {
              return "";
            }
          };
          const joinLimit = getJoiningDateString(emp.JoiningDate);
          if (joinLimit && dayStr < joinLimit) {
            return;
          }

          // Check approved leave
          const dayLeave = (leaveData?.data || []).find(
            (lv) =>
              lv.applicantName?.toLowerCase() === emp.Name?.toLowerCase() &&
              lv.Status === "Approved" &&
              lv.fromDate <= dayStr &&
              dayStr <= lv.toDate
          );

          const payrollId = getPayrollId(emp.Name);
          const dayPunches = payrollId ? (empPunchesByDate[payrollId] && empPunchesByDate[payrollId][dayStr]) || [] : [];
          const hasPunches = dayPunches.length > 0;

          let isPresent = false;
          if (dayLeave && dayLeave.LeaveType === "Half Day") {
            isPresent = true;
          } else if (hasPunches) {
            isPresent = true;
          }

          if (isPresent) {
            presentCount++;

            // check late arrival
            let firstIn = null;
            dayPunches.forEach((p) => {
              if (p.InDate) {
                if (!firstIn || p.InDate < firstIn) firstIn = p.InDate;
              }
            });
            const localInTime = firstIn ? getLocalTime(firstIn) : "";
            if (localInTime) {
              const lateDiff = getDiffMinutes(localInTime, emp.CheckInTime || "10:00");
              if (lateDiff > 0) {
                lateCount++;
              }
            }
          }
        });

        trends.push({
          day: dVal,
          date: dayStr,
          "Present Today": presentCount,
          "Late Arrivals": lateCount,
        });
      }
    }

    // 3. Leave Distribution (including owners)
    const leaveCounts = {};
    (leaveData?.data || []).forEach((lv) => {
      if (lv.Status === "Approved") {
        const type = lv.LeaveType || "Other";
        leaveCounts[type] = (leaveCounts[type] || 0) + 1;
      }
    });
    const leaveDist = Object.entries(leaveCounts).map(([name, count]) => ({
      name,
      count,
    }));

    // 4. Department Attendance
    const deptStats = {};
    attendanceAnalytics.forEach((emp) => {
      const dept = emp.department;
      if (!deptStats[dept]) {
        deptStats[dept] = { totalPct: 0, count: 0, totalHours: 0 };
      }
      deptStats[dept].totalPct += emp.monthlyStats.attendancePercentage;
      deptStats[dept].totalHours += emp.monthlyStats.workingMinutes / 60;
      deptStats[dept].count += 1;
    });

    const deptAttendance = Object.entries(deptStats).map(([name, stat]) => ({
      name,
      "Attendance %": Math.round(stat.totalPct / stat.count),
      "Avg Working Hours": Math.round((stat.totalHours / stat.count) * 10) / 10,
    }));

    return {
      pieData,
      trends,
      leaveDist,
      deptAttendance,
    };
  }, [kpis, attendanceAnalytics, activeFromDate, activeToDate, selectedDate, AllAttendanceData, leaveData, lateThreshold, selectedYear, selectedMonthVal, activeEmployees, getPayrollId]);

  // --- Reset All Filters ---
  const resetFilters = () => {
    setSelectedDepartment("All");
    setSelectedEmployee("All");
    setSelectedStatus("All");
    setShowOnlyLate(false);
    setShowOnlyEarly(false);
    setLeaveTypeFilter("All");
    setSearchQuery("");
  };

  // --- Export to CSV utilities ---
  const exportToCSV = (headers, rows, filename) => {
    if (!rows || rows.length === 0) {
      alert("No attendance records to export!");
      return;
    }
    const csvContent = [
      headers.map((h) => `"${String(h).replace(/"/g, '""')}"`).join(","),
      ...rows.map((e) => e.map((val) => `"${String(val ?? "").replace(/"/g, '""')}"`).join(",")),
    ].join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${filename}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // --- Export to PDF utilities ---
  const exportToPDF = () => {
    if (!filteredDashboardData || filteredDashboardData.length === 0) {
      alert("No attendance records to export!");
      return;
    }

    const doc = new jsPDF("landscape", "pt", "a4");
    const pageWidth = doc.internal.pageSize.getWidth(); // 841.89
    const pageHeight = doc.internal.pageSize.getHeight(); // 595.28

    // 1. Header Banner
    doc.setFillColor(30, 41, 59); // Sleek slate-850 dark banner
    doc.roundedRect(30, 20, 780, 50, 4, 4, "F");

    // Title & Subtitle
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("DYNAMIC MANAGEMENT SYSTEM", 45, 41);

    doc.setTextColor(199, 210, 254);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("Workforce Monthly Performance & Attendance Analytics Report", 45, 56);

    // Metadata Right Side
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(
      `Date Range: ${formatDateDMY(activeFromDate)} to ${formatDateDMY(activeToDate)}`,
      795,
      39,
      { align: "right" }
    );

    doc.setTextColor(203, 213, 225);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    const filterDept = selectedDepartment === "All" ? "All Departments" : `${selectedDepartment} Dept`;
    const filterStat = selectedStatus === "All" ? "All Statuses" : selectedStatus;
    doc.text(`Filter: ${filterDept}  |  ${filterStat}`, 795, 51, { align: "right" });
    const now = new Date();
    const genDate = now.toLocaleDateString("en-GB");
    const genTime = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    doc.text(`Generated: ${genDate} ${genTime}`, 795, 62, { align: "right" });

    // 2. KPI Summary Mini-Cards Row
    const cardW = 187;
    const cardGap = 10.6;
    const cardY = 78;
    const cardH = 34;

    const kpiCards = [
      {
        label: "TOTAL WORKFORCE",
        val: `${filteredDashboardData.length} Staff Members`,
        bg: [248, 250, 252],
        border: [226, 232, 240],
        text: [30, 41, 59],
      },
      {
        label: "AVG ATTENDANCE RATE",
        val: `${monthlyKPIs.attendancePercent}% Attendance`,
        bg: [238, 242, 255],
        border: [199, 210, 254],
        text: [67, 56, 202],
      },
      {
        label: "TOTAL LATE INCIDENTS",
        val: `${monthlyKPIs.late} Cases (${formatDeductionTime(monthlyKPIs.lateMinutes)})`,
        bg: [254, 243, 199],
        border: [251, 191, 36],
        text: [180, 83, 9],
      },
      {
        label: "TOTAL EARLY DEPARTURES",
        val: `${monthlyKPIs.early} Cases (${formatDeductionTime(monthlyKPIs.earlyMinutes)})`,
        bg: [254, 242, 242],
        border: [254, 202, 202],
        text: [190, 18, 60],
      },
    ];

    kpiCards.forEach((kpi, idx) => {
      const cardX = 30 + idx * (cardW + cardGap);
      doc.setFillColor(...kpi.bg);
      doc.setDrawColor(...kpi.border);
      doc.setLineWidth(0.7);
      doc.roundedRect(cardX, cardY, cardW, cardH, 3, 3, "FD");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      doc.setTextColor(100, 116, 139);
      doc.text(kpi.label, cardX + 8, cardY + 13);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.5);
      doc.setTextColor(...kpi.text);
      doc.text(kpi.val, cardX + 8, cardY + 27);
    });

    // 3. Table Headers and Body
    const headers = [
      [
        "#",
        "Employee Name",
        "Department",
        "Present",
        "Absent",
        "Leave",
        "Half Day",
        "Lates",
        "Late Time",
        "Earlies",
        "Early Out",
        "Total Deduction",
        "Hours Worked",
      ],
    ];

    let totalPresent = 0;
    let totalAbsent = 0;
    let totalLeave = 0;
    let totalHalfDay = 0;
    let totalLates = 0;
    let totalLateMinutes = 0;
    let totalEarlies = 0;
    let totalEarlyMinutes = 0;
    let totalWorkMinutes = 0;

    const rows = filteredDashboardData.map((emp, index) => {
      const st = emp.monthlyStats || {};
      const pres = st.presentDays || 0;
      const abs = st.absentDays || 0;
      const lv = st.leaveDays || 0;
      const hd = st.halfDays || 0;
      const ltCount = st.lateCount || 0;
      const ltMins = st.lateMinutes || 0;
      const erCount = st.earlyCount || 0;
      const erMins = st.earlyOutMinutes || 0;
      const totalDedMins = ltMins + erMins;
      const workMins = st.workingMinutes || 0;

      totalPresent += pres;
      totalAbsent += abs;
      totalLeave += lv;
      totalHalfDay += hd;
      totalLates += ltCount;
      totalLateMinutes += ltMins;
      totalEarlies += erCount;
      totalEarlyMinutes += erMins;
      totalWorkMinutes += workMins;

      return [
        String(index + 1),
        emp.name || "-",
        emp.department || "General",
        String(pres),
        String(abs),
        String(lv),
        String(hd),
        String(ltCount),
        formatDeductionTime(ltMins),
        String(erCount),
        formatDeductionTime(erMins),
        formatDeductionTime(totalDedMins),
        `${Math.round(workMins / 60)} hrs`,
      ];
    });

    const totalDedAll = totalLateMinutes + totalEarlyMinutes;
    const foot = [
      [
        "",
        "TOTAL / SUMMARY",
        `${filteredDashboardData.length} Staff`,
        String(totalPresent),
        String(totalAbsent),
        String(totalLeave),
        String(totalHalfDay),
        String(totalLates),
        formatDeductionTime(totalLateMinutes),
        String(totalEarlies),
        formatDeductionTime(totalEarlyMinutes),
        formatDeductionTime(totalDedAll),
        `${Math.round(totalWorkMinutes / 60)} hrs`,
      ],
    ];

    autoTable(doc, {
      startY: 120,
      head: headers,
      body: rows,
      foot: foot,
      margin: { left: 30, right: 30, bottom: 35 },
      tableWidth: 780,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: { top: 4, bottom: 4, left: 4, right: 4 },
        lineColor: [226, 232, 240],
        lineWidth: 0.5,
        valign: "middle",
      },
      headStyles: {
        fillColor: [30, 41, 59],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 8,
        halign: "center",
        minCellHeight: 22,
      },
      footStyles: {
        fillColor: [238, 242, 255],
        textColor: [30, 41, 59],
        fontStyle: "bold",
        fontSize: 8,
        halign: "center",
        minCellHeight: 20,
      },
      columnStyles: {
        0: { cellWidth: 25, halign: "center" },
        1: { cellWidth: 140, halign: "left", fontStyle: "bold" },
        2: { cellWidth: 75, halign: "left" },
        3: { cellWidth: 45, halign: "center", fontStyle: "bold", textColor: [22, 101, 52] },
        4: { cellWidth: 45, halign: "center", fontStyle: "bold", textColor: [225, 29, 72] },
        5: { cellWidth: 45, halign: "center", textColor: [79, 70, 229] },
        6: { cellWidth: 45, halign: "center", textColor: [217, 119, 6] },
        7: { cellWidth: 45, halign: "center", fontStyle: "bold", textColor: [180, 83, 9] },
        8: { cellWidth: 62, halign: "center", textColor: [180, 83, 9] },
        9: { cellWidth: 45, halign: "center", textColor: [71, 85, 105] },
        10: { cellWidth: 62, halign: "center", textColor: [71, 85, 105] },
        11: { cellWidth: 72, halign: "center", fontStyle: "bold", textColor: [220, 38, 38] },
        12: { cellWidth: 74, halign: "center", fontStyle: "bold", textColor: [30, 58, 138] },
      },
      didDrawPage: (data) => {
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);
        doc.setTextColor(148, 163, 184);
        doc.text(
          "DYNAMIC MANAGEMENT SYSTEM  •  Monthly Workforce Performance & Analytics",
          30,
          pageHeight - 15
        );
        doc.text(
          `Page ${data.pageNumber} of ${pageCount}`,
          pageWidth - 30,
          pageHeight - 15,
          { align: "right" }
        );
      },
    });

    const fileName = `Monthly_Performance_Report_${activeFromDate}_to_${activeToDate}.pdf`;
    doc.save(fileName);
  };

  if (allAttendanceLoading || availableMonths.length === 0) {
    return (
      <div className="h-screen w-full flex justify-center items-center bg-slate-50">
        <div className="h-24 w-24 flex items-center justify-center">
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full text-slate-800 font-sans relative pt-2 space-y-6 pb-6">
      {/* Page Title */}
      <div className="title mb-4">
        <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
          Attendance Analytics Dashboard
        </h3>
      </div>

      {/* ============================================================== */}
      {/* 📅 TOP SECTION: TODAY'S REAL-TIME ATTENDANCE */}
      {/* ============================================================== */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-md p-6 relative overflow-hidden">
        {/* Decorative corner glow */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-400/5 blur-3xl rounded-full -mr-20 -mt-20 pointer-events-none"></div>

        {/* Section Heading */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between border-b border-slate-100 pb-5 mb-6 gap-3">
          <div>
            <h2 className="text-lg font-black text-slate-900 flex items-center gap-2">
              <span className={`h-2.5 w-2.5 rounded-full ${todayFilterDate === new Date().toISOString().slice(0, 10) ? "bg-emerald-500 animate-ping" : "bg-indigo-500"}`}></span>
              {todayFilterDate === new Date().toISOString().slice(0, 10) ? "Today's Attendance Overview" : "Single Date Attendance Overview"}
            </h2>
            <p className="text-slate-600 text-xs mt-0.5 font-bold">
              Live checks for {formatTodayHeaderDate(todayFilterDate)}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {todayLoading && (
              <span className="text-xs text-indigo-605 bg-indigo-50 px-2.5 py-1 rounded-lg border border-indigo-150 font-bold">
                Updating real-time logs...
              </span>
            )}
            <button
              onClick={() => fetchTodayAttendance(todayFilterDate)}
              disabled={todayLoading}
              className="flex items-center gap-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold border border-indigo-200 px-4 py-2 rounded-xl text-xs transition-all shadow-xs cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiRefreshCw className={`h-3 w-3 ${todayLoading ? "animate-spin" : ""}`} />
              {todayFilterDate === new Date().toISOString().slice(0, 10) ? "Sync Today" : "Sync Selected Date"}
            </button>
          </div>
        </div>

        {/* Today's KPI Metric Cards Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {/* Total Employees */}
          <div
            onClick={() => setTodayStatus("All")}
            className={`border rounded-2xl p-4 shadow-xs transition-all hover:shadow-md cursor-pointer hover:scale-[1.02] ${todayStatus === "All"
              ? "border-slate-500 ring-2 ring-slate-400/25 bg-slate-100/80 font-bold scale-[1.02]"
              : "bg-slate-50/60 border-slate-150/40 hover:bg-slate-50"
              }`}
          >
            <span className="text-slate-400 text-[10px] font-black uppercase tracking-wider block font-bold">Workforce</span>
            <div className="flex items-center justify-between mt-2.5">
              <h3 className="text-2xl font-black text-slate-900 leading-none">{todayKpis.total}</h3>
              <div className="h-8 w-8 rounded-lg bg-indigo-600/10 flex items-center justify-center text-indigo-600">
                <FiUsers size={16} />
              </div>
            </div>
          </div>

          {/* Present */}
          <div
            onClick={() => setTodayStatus("Present")}
            className={`border rounded-2xl p-4 shadow-xs transition-all hover:shadow-md cursor-pointer hover:scale-[1.02] ${todayStatus === "Present"
              ? "border-emerald-500 ring-2 ring-emerald-400/25 bg-emerald-100/50 font-bold scale-[1.02]"
              : "bg-emerald-50/30 border-emerald-150/30 hover:bg-emerald-50/50"
              }`}
          >
            <span className="text-slate-400 text-[10px] font-black uppercase tracking-wider block font-bold">Present</span>
            <div className="flex items-center justify-between mt-2.5">
              <h3 className="text-2xl font-black text-emerald-600 leading-none">{todayKpis.present}</h3>
              <div className="h-8 w-8 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
                <FiUserCheck size={16} />
              </div>
            </div>
          </div>

          {/* Absent */}
          <div
            onClick={() => setTodayStatus("Absent")}
            className={`border rounded-2xl p-4 shadow-xs transition-all hover:shadow-md cursor-pointer hover:scale-[1.02] ${todayStatus === "Absent"
              ? "border-rose-500 ring-2 ring-rose-400/25 bg-rose-100/50 font-bold scale-[1.02]"
              : "bg-rose-50/30 border-rose-150/30 hover:bg-rose-50/50"
              }`}
          >
            <span className="text-slate-400 text-[10px] font-black uppercase tracking-wider block font-bold">Absent</span>
            <div className="flex items-center justify-between mt-2.5">
              <h3 className="text-2xl font-black text-rose-650 leading-none">{todayKpis.absent}</h3>
              <div className="h-8 w-8 rounded-lg bg-rose-600/10 flex items-center justify-center text-rose-600">
                <FiUserX size={16} />
              </div>
            </div>
          </div>

          {/* Late */}
          <div
            onClick={() => setTodayStatus("Late")}
            className={`border rounded-2xl p-4 shadow-xs transition-all hover:shadow-md cursor-pointer hover:scale-[1.02] ${todayStatus === "Late"
              ? "border-amber-500 ring-2 ring-amber-400/25 bg-amber-100/50 font-bold scale-[1.02]"
              : "bg-amber-50/30 border-amber-150/30 hover:bg-amber-50/55"
              }`}
          >
            <span className="text-slate-400 text-[10px] font-black uppercase tracking-wider block font-bold">Late Arrival</span>
            <div className="flex items-center justify-between mt-2.5">
              <h3 className="text-2xl font-black text-amber-600 leading-none">{todayKpis.late}</h3>
              <div className="h-8 w-8 rounded-lg bg-amber-50 flex items-center justify-center text-amber-600">
                <FiClock size={16} />
              </div>
            </div>
          </div>

          {/* Approved Leaves */}
          <div
            onClick={() => setTodayStatus("Leave")}
            className={`border rounded-2xl p-4 shadow-xs transition-all hover:shadow-md cursor-pointer hover:scale-[1.02] ${todayStatus === "Leave"
              ? "border-indigo-500 ring-2 ring-indigo-400/25 bg-indigo-100/50 font-bold scale-[1.02]"
              : "bg-indigo-50/20 border-indigo-150/20 hover:bg-indigo-50/40"
              }`}
          >
            <span className="text-slate-400 text-[10px] font-black uppercase tracking-wider block font-bold">On Leave</span>
            <div className="flex items-center justify-between mt-2.5">
              <h3 className="text-2xl font-black text-indigo-600 leading-none">{todayKpis.onLeave}</h3>
              <div className="h-8 w-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
                <FiCalendar size={16} />
              </div>
            </div>
          </div>
          {/* Half Day */}
          <div
            onClick={() => setTodayStatus("Half Day")}
            className={`border rounded-2xl p-4 shadow-xs transition-all hover:shadow-md cursor-pointer hover:scale-[1.02] ${todayStatus === "Half Day"
              ? "border-orange-500 ring-2 ring-orange-400/25 bg-orange-100/50 font-bold scale-[1.02]"
              : "bg-orange-50/30 border-orange-150/30 hover:bg-orange-50/50"
              }`}
          >
            <span className="text-slate-400 text-[10px] font-black uppercase tracking-wider block font-bold">Half Day</span>
            <div className="flex items-center justify-between mt-2.5">
              <h3 className="text-2xl font-black text-orange-655 leading-none">{todayKpis.halfDay}</h3>
              <div className="h-8 w-8 rounded-lg bg-orange-50 flex items-center justify-center text-orange-500">
                <FiTrendingUp size={16} />
              </div>
            </div>
          </div>
        </div>

        {/* Filters specific to Today's view */}
        <div className="bg-slate-50 border border-slate-200/50 rounded-2xl p-4 mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
          {/* Selected Date */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wide">Select Date</label>
            <input
              type="date"
              value={todayFilterDate}
              onChange={(e) => {
                if (e.target.value) {
                  setTodayFilterDate(e.target.value);
                }
              }}
              className="w-full px-3 py-1.5 bg-white border border-slate-200 rounded-xl font-bold text-slate-755 focus:outline-none focus:ring-1 focus:ring-indigo-500/25 focus:border-indigo-500/50 text-xs h-9 transition-all cursor-pointer"
            />
          </div>

          {/* Search Employee */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wide">Search Employee</label>
            <div className="relative">
              <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search name or role..."
                value={todaySearch}
                onChange={(e) => setTodaySearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-500/25 focus:border-indigo-500/50 text-xs h-9 transition-all"
              />
            </div>
          </div>

          {/* Department selection */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wide">Department</label>
            <select
              value={todayDept}
              onChange={(e) => setTodayDept(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-slate-700 font-bold text-xs h-9 focus:outline-none focus:ring-1 focus:ring-indigo-500/25 focus:border-indigo-500/50 transition-all cursor-pointer"
            >
              {departments.map((d) => (
                <option key={d} value={d}>{d === "All" ? "All Departments" : `${d} Department`}</option>
              ))}
            </select>
          </div>

          {/* Status selection */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wide">Status</label>
            <select
              value={todayStatus}
              onChange={(e) => setTodayStatus(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-slate-700 font-bold text-xs h-9 focus:outline-none focus:ring-1 focus:ring-indigo-500/25 focus:border-indigo-500/50 transition-all cursor-pointer"
            >
              <option value="All">All Statuses</option>
              <option value="Present">Present</option>
              <option value="Absent">Absent</option>
              <option value="Late">Late Arrival</option>
              <option value="Leave">On Approved Leave</option>
              <option value="Half Day">Half Day</option>
            </select>
          </div>
        </div>

        {/* Today's Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Today's Table */}
          <div className="xl:col-span-2 border border-slate-150/60 rounded-2xl bg-white p-4 h-[600px] overflow-hidden flex flex-col shadow-xs">
            <div className="flex-1">
              <DynamicTable
                columns={todayColumns}
                rows={filteredTodayData}
                searchable={false}
                height={520}
                emptyComponent={
                  <div className="flex flex-col items-center justify-center h-full text-slate-450 py-12">
                    <FiUserCheck size={36} className="text-emerald-500 mb-2" />
                    <p className="font-bold text-slate-700 text-sm">No matching entries</p>
                    <p className="text-xs text-slate-400 mt-1">There are no records for today matching your filters.</p>
                  </div>
                }
              />
            </div>
          </div>

          {/* Today's Late Audits Panel */}
          <div className="border border-slate-150/60 rounded-2xl bg-white p-5 h-[600px] flex flex-col shadow-xs">
            <div className="flex items-center gap-2 mb-4 border-b border-slate-100 pb-3.5 flex-shrink-0">
              <FiAlertCircle className="text-amber-500" size={16} />
              <h3 className="font-extrabold text-slate-900 text-sm">Today's Late Check-Ins</h3>
            </div>
            <div className="flex-1 overflow-y-auto pr-1 scrollbar-thin divide-y divide-slate-100">
              {todayAnalytics
                .filter((e) => e.isLate)
                .map((emp) => {
                  const lateMins = getDiffMinutes(emp.inTime, emp.checkInTime || "10:00");
                  return { ...emp, lateMins };
                })
                .sort((a, b) => b.lateMins - a.lateMins)
                .map((emp) => {
                  const lateMins = emp.lateMins;
                  const matchedEmp = attendanceAnalytics.find((e) => e.name === emp.name);
                  const monthlyLateCount = matchedEmp ? matchedEmp.monthlyStats?.lateCount || 0 : 0;
                  return (
                    <div
                      key={emp.id}
                      onClick={() => {
                        if (matchedEmp) {
                          handleShowPerformanceDetails(matchedEmp, "late");
                        }
                      }}
                      className="py-2 flex items-center justify-between border-l-4 border-l-amber-400 pl-3 bg-slate-50/40 rounded-r-xl mb-1 transition-all hover:bg-slate-50 cursor-pointer hover:border-amber-500"
                    >
                      <div className="flex-1 min-w-0 pr-2">
                        <h4 className="font-bold text-slate-800 text-[13px] truncate leading-tight">{emp.name}</h4>
                        <p className="text-[10px] text-slate-550 mt-0.5 font-semibold">
                          Punch In: <span className="font-bold text-rose-600">{emp.inTime}</span>
                          <span className="mx-1.5 text-slate-350">|</span>
                          {emp.role}
                        </p>
                        {monthlyLateCount > 0 && (
                          <div className="mt-1">
                            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-amber-800 bg-amber-50 border border-amber-200/50 px-2 py-0.5 rounded-lg whitespace-nowrap">
                              <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                              Late {monthlyLateCount} times this month ({formatDeductionTime(matchedEmp.monthlyStats?.lateMinutes || 0)})
                            </span>
                          </div>
                        )}
                      </div>
                      <span className="text-xs font-black text-rose-600 bg-rose-50 px-2 py-1 rounded border border-rose-105 font-mono whitespace-nowrap">
                        +{lateMins} mins
                      </span>
                    </div>
                  );
                })}
              {todayAnalytics.filter(e => e.isLate).length === 0 && (
                <div className="text-center text-slate-455 py-32">
                  <FiUserCheck className="mx-auto text-emerald-500 mb-3 animate-bounce" size={36} />
                  <p className="font-bold text-slate-700 text-sm">Perfect Punctuality</p>
                  <p className="text-xs text-slate-400 mt-1">No employees checked in late today.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ============================================================== */}
      {/* 📊 BOTTOM SECTION: MONTHLY PERFORMANCE & ANALYTICS */}
      {/* ============================================================== */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-md p-6 relative overflow-hidden">
        {/* Section Heading */}
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between border-b border-slate-100 pb-5 mb-6 gap-4">
          <div>
            <h2 className="text-lg font-black text-slate-900 flex items-center gap-2">
              <FiCalendar className="text-indigo-650" />
              Monthly Performance & Analytics
            </h2>
            <p className="text-slate-400 text-xs mt-0.5">
              Historical audits, scorecard rankings, daily trends, and department analytics.
            </p>
          </div>

          {/* Monthly controls */}
          <div className="flex flex-col sm:flex-row sm:items-end gap-3 flex-shrink-0">
            {/* Month Select */}
            <div className="flex flex-col">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">Select Month</label>
              <select
                value={`${selectedYear}-${selectedMonthVal}`}
                onChange={(e) => {
                  const val = e.target.value;
                  if (!val) return;
                  const [y, m] = val.split("-");
                  setSelectedYear(y);
                  setSelectedMonthVal(m);
                  const firstDay = `${y}-${m}-01`;
                  const todayStr = new Date().toISOString().slice(0, 10);
                  const lastDayNum = new Date(Number(y), Number(m), 0).getDate();
                  let lastDay = `${y}-${m}-${String(lastDayNum).padStart(2, "0")}`;
                  if (todayStr.startsWith(`${y}-${m}`)) {
                    lastDay = todayStr;
                  }
                  setTempFromDate(firstDay);
                  setTempToDate(lastDay);
                  setActiveFromDate(firstDay);
                  setActiveToDate(lastDay);
                }}
                className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 font-bold text-xs h-9 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer w-40"
              >
                {availableMonths.map((m) => (
                  <option key={m} value={m}>{getMonthLabel(m)}</option>
                ))}
              </select>
            </div>
            {/* Custom Range picker inputs */}
            <div className="flex flex-row gap-2">
              <div className="flex flex-col">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">From</label>
                <input
                  type="date"
                  value={tempFromDate}
                  onChange={(e) => setTempFromDate(e.target.value)}
                  className="px-2.5 py-1 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 font-bold text-xs h-9"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">To</label>
                <input
                  type="date"
                  value={tempToDate}
                  onChange={(e) => setTempToDate(e.target.value)}
                  className="px-2.5 py-1 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 font-bold text-xs h-9"
                />
              </div>
            </div>
            <button
              onClick={handleApplyFilter}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-9 px-4 rounded-xl text-xs transition-colors shadow-sm flex items-center justify-center gap-1.5 cursor-pointer border border-emerald-500 self-end"
            >
              Apply Range
            </button>
            <button
              onClick={exportToPDF}
              className="bg-rose-600 hover:bg-rose-700 text-white font-bold h-9 px-3.5 rounded-xl text-xs transition-colors shadow-sm flex items-center justify-center gap-1.5 cursor-pointer border border-rose-500 self-end"
              title="Download Monthly Performance & Analytics PDF Report"
            >
              <FiPrinter size={13} />
              Export PDF
            </button>
          </div>
        </div>

        {/* Advanced Monthly Filters */}
        <div className="bg-slate-50 border border-slate-200/50 rounded-2xl p-4 mb-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 items-end">
          {/* Search Employee */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wide">Search Employee</label>
            <div className="relative">
              <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
              <input
                type="text"
                placeholder="Search name or role..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-xl pl-9 pr-3 py-1.5 text-xs font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:border-indigo-500/60 focus:ring-1 focus:ring-indigo-500/20 transition-all shadow-xs"
              />
            </div>
          </div>

          {/* Department Selection */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wide">Department</label>
            <select
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-bold text-slate-755 focus:outline-none focus:border-indigo-500/60 focus:ring-1 focus:ring-indigo-500/20 transition-all shadow-xs cursor-pointer"
            >
              {departments.map((dept) => (
                <option key={dept} value={dept}>
                  {dept} Department
                </option>
              ))}
            </select>
          </div>

          {/* Status Selection */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wide">Filter Status (Any days in range)</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-bold text-slate-755 focus:outline-none focus:border-indigo-500/60 focus:ring-1 focus:ring-indigo-500/20 transition-all shadow-xs cursor-pointer"
            >
              <option value="All">All Statuses</option>
              <option value="Present">Present</option>
              <option value="Absent">Absent</option>
              <option value="Late">Late Arrivals</option>
              <option value="Early">Early Out</option>
              <option value="Leave">On Leave</option>
              <option value="Half Day">Half Day</option>
            </select>
          </div>

          {/* Reset Filters button */}
          <div className="flex items-center">
            {(searchQuery !== "" || selectedDepartment !== "All" || selectedStatus !== "All") && (
              <button
                onClick={resetFilters}
                className="flex items-center justify-center gap-1.5 text-xs text-rose-600 hover:text-rose-800 bg-rose-50 hover:bg-rose-100/80 px-4 py-2 rounded-xl border border-rose-150 transition-all font-bold w-full h-9 shadow-xs cursor-pointer"
              >
                <FiRefreshCw size={12} className="animate-spin-slow" />
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Monthly Summary Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 max-w-4xl">
          <div className="bg-indigo-50/15 border border-indigo-150/20 rounded-2xl p-4 shadow-xs flex flex-col justify-between h-[115px]">
            <div>
              <span className="text-slate-400 text-[10px] font-black uppercase tracking-wider block font-bold">Avg Attendance Rate</span>
              <h3 className="text-2xl font-black text-indigo-700 mt-1 leading-none">{monthlyKPIs.attendancePercent}%</h3>
            </div>
            <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden mt-2">
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${monthlyKPIs.attendancePercent}%`, backgroundColor: '#4f46e5' }}></div>
            </div>
          </div>
          <div className="bg-slate-50 border border-slate-150/40 rounded-2xl p-4 shadow-xs flex flex-col justify-between h-[115px]">
            <div>
              <span className="text-slate-400 text-[10px] font-black uppercase tracking-wider block font-bold">Total Late Incidents</span>
              <div className="flex items-baseline justify-between mt-1.5">
                <h3 className="text-2xl font-black text-amber-600 leading-none">{monthlyKPIs.late} Cases</h3>
                <span className="text-[11px] font-bold text-slate-500 font-mono bg-amber-50 px-2 py-0.5 rounded border border-amber-100/50 whitespace-nowrap">
                  {formatDeductionTime(monthlyKPIs.lateMinutes)}
                </span>
              </div>
            </div>
            <span className="text-[9px] text-slate-450 block tracking-wide font-medium mt-1">Checked in after threshold</span>
          </div>
          <div className="bg-slate-50 border border-slate-150/40 rounded-2xl p-4 shadow-xs flex flex-col justify-between h-[115px]">
            <div>
              <span className="text-slate-400 text-[10px] font-black uppercase tracking-wider block font-bold">Total Early Departures</span>
              <div className="flex items-baseline justify-between mt-1.5">
                <h3 className="text-2xl font-black text-rose-600 leading-none">{monthlyKPIs.early} Cases</h3>
                <span className="text-[11px] font-bold text-slate-500 font-mono bg-rose-50 px-2 py-0.5 rounded border border-rose-100/50 whitespace-nowrap">
                  {formatDeductionTime(monthlyKPIs.earlyMinutes)}
                </span>
              </div>
            </div>
            <span className="text-[9px] text-slate-450 block tracking-wide font-medium mt-1">Checked out before threshold</span>
          </div>
        </div>

        {/* Tab Navigation Menu */}
        <div className="bg-slate-100/60 border border-slate-200/50 p-1.5 rounded-2xl flex flex-wrap gap-1.5 mb-6 w-max max-w-full">
          <button
            onClick={() => setActiveTab("spreadsheet")}
            className={`px-5 py-2.5 rounded-xl font-extrabold text-xs tracking-wide transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 ${activeTab === "spreadsheet"
              ? "bg-white text-indigo-700 shadow-xs border border-slate-200/40"
              : "text-slate-500 hover:text-slate-800 hover:bg-white/40"
              }`}
          >
            <span>📊</span> Detailed Spreadsheet
          </button>
          <button
            onClick={() => setActiveTab("rankings")}
            className={`px-5 py-2.5 rounded-xl font-extrabold text-xs tracking-wide transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 ${activeTab === "rankings"
              ? "bg-white text-indigo-700 shadow-xs border border-slate-200/40"
              : "text-slate-500 hover:text-slate-800 hover:bg-white/40"
              }`}
          >
            <span>🏆</span> Performance Scorecard
          </button>
          <button
            onClick={() => setActiveTab("analytics")}
            className={`px-5 py-2.5 rounded-xl font-extrabold text-xs tracking-wide transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 ${activeTab === "analytics"
              ? "bg-white text-indigo-700 shadow-xs border border-slate-200/40"
              : "text-slate-500 hover:text-slate-800 hover:bg-white/40"
              }`}
          >
            <span>📈</span> Trends & Analytics
          </button>
          <button
            onClick={() => setActiveTab("leaves")}
            className={`px-5 py-2.5 rounded-xl font-extrabold text-xs tracking-wide transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 ${activeTab === "leaves"
              ? "bg-white text-indigo-700 shadow-xs border border-slate-200/40"
              : "text-slate-500 hover:text-slate-800 hover:bg-white/40"
              }`}
          >
            <span>🌴</span> Leave Diagnostics
          </button>

        </div>

        {/* Tab Content Rendering */}
        <div className="min-h-[400px]">
          {/* Tab 1: Detailed Spreadsheet */}
          {activeTab === "spreadsheet" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-2 flex-wrap bg-slate-50 border border-slate-200/50 rounded-2xl p-4 shadow-xs">
                <span className="text-xs font-bold text-slate-600">
                  Spreadsheet Report for range {formatDateDMY(activeFromDate)} to {formatDateDMY(activeToDate)}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={exportToPDF}
                    className="text-xs text-rose-600 hover:text-rose-800 font-bold flex items-center gap-1.5 bg-rose-50 hover:bg-rose-100 px-3.5 py-2 rounded-xl border border-rose-150 h-9 transition-colors cursor-pointer shadow-xs"
                    title="Export Monthly Performance & Analytics PDF Report"
                  >
                    <FiPrinter size={13} />
                    Export PDF
                  </button>
                  <button
                    onClick={() =>
                      exportToCSV(
                        [
                          "Name",
                          "Department",
                          "Present Days",
                          "Absent Days",
                          "Leave Days",
                          "Half Days",
                          "Late Arrival Days",
                          "Late Duration",
                          "Early Out Days",
                          "Early Departure Duration",
                          "Total Deduction",
                          "Total Working Hours",
                        ],
                        filteredDashboardData.map((emp) => [
                          emp.name,
                          emp.department,
                          emp.monthlyStats.presentDays,
                          emp.monthlyStats.absentDays,
                          emp.monthlyStats.leaveDays,
                          emp.monthlyStats.halfDays,
                          emp.monthlyStats.lateCount,
                          formatDeductionTime(emp.monthlyStats.lateMinutes),
                          emp.monthlyStats.earlyCount,
                          formatDeductionTime(emp.monthlyStats.earlyOutMinutes),
                          formatDeductionTime(emp.monthlyStats.lateMinutes + emp.monthlyStats.earlyOutMinutes),
                          Math.round(emp.monthlyStats.workingMinutes / 60),
                        ]),
                        `Monthly_Attendance_Stats_${activeFromDate}_to_${activeToDate}`
                      )
                    }
                    className="text-xs text-indigo-600 hover:text-indigo-850 font-bold flex items-center gap-1.5 bg-indigo-50 hover:bg-indigo-100 px-3.5 py-2 rounded-xl border border-indigo-100 h-9 transition-colors cursor-pointer shadow-xs"
                  >
                    <FiDownload size={13} />
                    Export Sheet (CSV)
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <DynamicTable
                  columns={statisticsColumns}
                  rows={filteredDashboardData}
                  searchable={false}
                  height={480}
                  emptyComponent={
                    <div className="flex flex-col items-center justify-center h-full text-slate-400 py-16">
                      <FiUserCheck size={36} className="text-emerald-500 mb-2" />
                      <p className="font-bold text-slate-600 text-sm">No statistics available</p>
                    </div>
                  }
                />
              </div>
            </div>
          )}

          {/* Tab 2: Performance Scorecard */}
          {activeTab === "rankings" && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {/* Most Late Arrivals */}
              <div className="bg-slate-50/50 border border-slate-150/40 rounded-2xl p-4 shadow-sm flex flex-col h-[360px]">
                <h4 className="text-xs font-black text-slate-550 mb-3.5 text-center uppercase tracking-wide flex-shrink-0 pb-2 border-b border-slate-200/50">Most Late Arrivals</h4>
                <div className="flex-1 overflow-y-auto pr-1 scrollbar-thin flex flex-col gap-2">
                  {rankings.mostLate.map((emp, i) => (
                    <div
                      key={emp.id}
                      onClick={() => handleShowPerformanceDetails(emp, "late")}
                      className="flex justify-between items-center bg-white p-3 rounded-xl border border-slate-100 hover:border-amber-300 hover:bg-slate-50/30 transition-all shadow-sm flex-shrink-0 cursor-pointer"
                    >
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-slate-700">
                          {i + 1}. {emp.name}
                        </span>
                        <span className="text-[10px] text-slate-700 font-bold font-mono mt-0.5 pl-3.5">
                          Total Time: {formatDeductionTime(emp.monthlyStats.lateMinutes)}
                        </span>
                      </div>
                      <span className="text-xs font-black text-rose-600 bg-rose-50 px-2.5 py-1 rounded border border-rose-100/50">
                        {emp.monthlyStats.lateCount} Days
                      </span>
                    </div>
                  ))}
                  {rankings.mostLate.length === 0 && (
                    <p className="text-xs text-slate-400 text-center py-10 font-medium flex-shrink-0">No late check-ins recorded.</p>
                  )}
                </div>
              </div>

              {/* Top Early Outs */}
              <div className="bg-slate-50/50 border border-slate-150/40 rounded-2xl p-4 shadow-sm flex flex-col h-[360px]">
                <h4 className="text-xs font-black text-slate-550 mb-3.5 text-center uppercase tracking-wide flex-shrink-0 pb-2 border-b border-slate-200/50">Top Early Departures</h4>
                <div className="flex-1 overflow-y-auto pr-1 scrollbar-thin flex flex-col gap-2">
                  {rankings.mostEarlyOuts.map((emp, i) => (
                    <div
                      key={emp.id}
                      onClick={() => handleShowPerformanceDetails(emp, "early")}
                      className="flex justify-between items-center bg-white p-3 rounded-xl border border-slate-100 hover:border-rose-300 hover:bg-slate-50/30 transition-all shadow-sm flex-shrink-0 cursor-pointer"
                    >
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-slate-700">
                          {i + 1}. {emp.name}
                        </span>
                        <span className="text-[10px] text-slate-700 font-bold font-mono mt-0.5 pl-3.5">
                          Total Time: {formatDeductionTime(emp.monthlyStats.earlyOutMinutes)}
                        </span>
                      </div>
                      <span className="text-xs font-black text-rose-600 bg-rose-50 px-2.5 py-1 rounded border border-rose-100/50">
                        {emp.monthlyStats.earlyCount} Days
                      </span>
                    </div>
                  ))}
                  {rankings.mostEarlyOuts.length === 0 && (
                    <p className="text-xs text-slate-400 text-center py-10 font-medium flex-shrink-0">No early departures recorded.</p>
                  )}
                </div>
              </div>

              {/* Top Absents */}
              <div className="bg-slate-50/50 border border-slate-150/40 rounded-2xl p-4 shadow-sm flex flex-col h-[360px]">
                <h4 className="text-xs font-black text-slate-550 mb-3.5 text-center uppercase tracking-wide flex-shrink-0 pb-2 border-b border-slate-200/50">Top Absents</h4>
                <div className="flex-1 overflow-y-auto pr-1 scrollbar-thin flex flex-col gap-2">
                  {rankings.mostAbsents.map((emp, i) => (
                    <div
                      key={emp.id}
                      onClick={() => handleShowPerformanceDetails(emp, "absent")}
                      className="flex justify-between items-center bg-white p-3 rounded-xl border border-slate-100 hover:border-indigo-300 hover:bg-slate-50/30 transition-all shadow-sm flex-shrink-0 cursor-pointer"
                    >
                      <span className="text-xs font-bold text-slate-700">
                        {i + 1}. {emp.name}
                      </span>
                      <span className="text-xs font-black text-rose-600 bg-rose-50 px-2.5 py-1 rounded border border-rose-100/50 font-mono">
                        {emp.monthlyStats.absentDays} Days
                      </span>
                    </div>
                  ))}
                  {rankings.mostAbsents.length === 0 && (
                    <p className="text-xs text-slate-400 text-center py-10 font-medium flex-shrink-0">No absent days recorded.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Tab 3: Analytics & Trends */}
          {activeTab === "analytics" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Daily Trend */}
              <div className="bg-white border border-slate-200/60 rounded-2xl p-5 hover:shadow-md transition-all">
                <h3 className="text-xs font-bold text-slate-800 mb-4 flex items-center justify-between">
                  <span>Daily Attendance & Late Trend</span>
                  <span className="text-[10px] font-bold text-slate-450 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-lg">
                    {getMonthLabel(`${selectedYear}-${selectedMonthVal}`)}
                  </span>
                </h3>
                <div className="h-[280px] w-full relative">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartsData.trends}>
                      <defs>
                        <linearGradient id="colorPresent" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10B981" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="colorLate" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                      <XAxis dataKey="day" stroke="#94A3B8" fontSize={11} tickLine={false} />
                      <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} />
                      <Tooltip contentStyle={{ borderRadius: "12px", border: "1px solid #E2E8F0" }} />
                      <Legend />
                      <Area type="monotone" dataKey="Present Today" stroke="#10B981" strokeWidth={2} fillOpacity={1} fill="url(#colorPresent)" />
                      <Area type="monotone" dataKey="Late Arrivals" stroke="#F59E0B" strokeWidth={2} fillOpacity={1} fill="url(#colorLate)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Status Mix */}
              <div className="bg-white border border-slate-200/60 rounded-2xl p-5 flex flex-col sm:flex-row items-center hover:shadow-md transition-all">
                <div className="flex-1 w-full">
                  <h3 className="text-xs font-bold text-slate-800 mb-4 flex items-center justify-between">
                    <span>Attendance Status Distribution</span>
                  </h3>
                  <div className="h-[240px] w-full relative">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={chartsData.pieData.filter((d) => d.value > 0)}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={4}
                          dataKey="value"
                        >
                          {chartsData.pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ borderRadius: "12px", border: "1px solid #E2E8F0" }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                {/* Legend list */}
                <div className="flex flex-col gap-3.5 mt-4 sm:mt-0 w-full sm:w-auto px-4 pl-6 border-l-0 sm:border-l border-slate-100">
                  {chartsData.pieData.map((d, index) => (
                    <div key={index} className="flex items-center gap-3 w-40">
                      <span className="h-3 w-3 rounded-md" style={{ backgroundColor: d.color }}></span>
                      <span className="text-xs font-bold text-slate-700">{d.name}</span>
                      <span className="text-xs text-slate-400 font-extrabold ml-auto font-mono">{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Tab 4: Leave Diagnostics */}
          {activeTab === "leaves" && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {/* Active Leave List */}
              <div className="bg-slate-50/50 border border-slate-150/40 rounded-2xl p-4 shadow-sm flex flex-col h-[360px]">
                <h4 className="text-xs font-black text-slate-550 mb-3.5 text-center uppercase tracking-wide flex-shrink-0 pb-2 border-b border-slate-200/50">
                  Active Leaves Today
                </h4>
                <div className="flex-1 overflow-y-auto pr-1 scrollbar-thin flex flex-col gap-2">
                  {todayAnalytics
                    .filter((emp) => emp.status === "Leave" || emp.status === "Half Day" || emp.leaveType)
                    .map((emp) => (
                      <div key={emp.id} className="py-2 px-3 bg-white border border-slate-100 rounded-xl flex items-center justify-between">
                        <div>
                          <span className="font-bold text-slate-800 text-xs">{emp.name}</span>
                          <span className="block text-[10px] text-slate-400">{emp.role}</span>
                        </div>
                        <span className={`text-[9px] font-black px-2 py-0.5 rounded border ${emp.status === "Half Day"
                          ? "bg-amber-50 text-amber-700 border-amber-100"
                          : emp.status === "Present"
                            ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                            : "bg-indigo-50 text-indigo-700 border-indigo-100"
                          }`}>
                          {emp.status === "Present" ? `Present (${emp.leaveType})` : (emp.leaveType || emp.status)}
                        </span>
                      </div>
                    ))}
                  {todayAnalytics.filter((emp) => emp.status === "Leave" || emp.status === "Half Day" || emp.leaveType).length === 0 && (
                    <p className="text-xs text-slate-400 text-center py-10 font-medium">No employees on leave today.</p>
                  )}
                </div>
              </div>

              {/* Pending Approvals */}
              <div className="bg-slate-50/50 border border-slate-150/40 rounded-2xl p-4 shadow-sm flex flex-col h-[360px]">
                <h4 className="text-xs font-black text-slate-550 mb-3.5 text-center uppercase tracking-wide flex-shrink-0 pb-2 border-b border-slate-200/50">
                  Pending Approval List
                </h4>
                <div className="flex-1 overflow-y-auto pr-1 scrollbar-thin flex flex-col gap-2">
                  {pendingApprovals.map((lv) => (
                    <div key={lv._id} className="py-2.5 px-3 bg-white border border-slate-100 rounded-xl shadow-xs">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-slate-800 text-xs">{lv.applicantName}</span>
                        <span className="text-[9px] font-extrabold bg-amber-50 text-amber-700 border border-amber-100 px-1.5 py-0.5 rounded">{lv.LeaveType}</span>
                      </div>
                      <span className="block text-[10px] text-indigo-650 font-mono mt-1 font-bold">
                        {lv.fromDate} to {lv.toDate} ({lv.TotalDays} Days)
                      </span>
                      {lv.Reason && (
                        <span className="block text-[9.5px] text-slate-450 mt-1 italic">
                          Reason: {lv.Reason}
                        </span>
                      )}
                    </div>
                  ))}
                  {pendingApprovals.length === 0 && (
                    <p className="text-xs text-slate-400 text-center py-10 font-medium">No pending requests.</p>
                  )}
                </div>
              </div>

              {/* Upcoming Leaves */}
              <div className="bg-slate-50/50 border border-slate-150/40 rounded-2xl p-4 shadow-sm flex flex-col h-[360px]">
                <h4 className="text-xs font-black text-slate-550 mb-3.5 text-center uppercase tracking-wide flex-shrink-0 pb-2 border-b border-slate-200/50">
                  Upcoming Approved Leaves
                </h4>
                <div className="flex-1 overflow-y-auto pr-1 scrollbar-thin flex flex-col gap-2">
                  {upcomingLeaves.map((lv) => (
                    <div key={lv._id} className="py-2.5 px-3 bg-white border border-slate-100 rounded-xl shadow-xs">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-slate-800 text-xs">{lv.applicantName}</span>
                        <span className="text-[9px] text-emerald-700 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded font-extrabold">
                          Approved
                        </span>
                      </div>
                      <span className="block text-[10px] text-slate-550 mt-1 font-mono">
                        {lv.fromDate} to {lv.toDate} ({lv.TotalDays} Days)
                      </span>
                    </div>
                  ))}
                  {upcomingLeaves.length === 0 && (
                    <p className="text-xs text-slate-400 text-center py-10 font-medium">No upcoming approved leaves.</p>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ============================================================== */}
      {/* 🔍 DETAILED EMPLOYEE PERFORMANCE TIMELINE MODAL */}
      {/* ============================================================== */}
      {selectedPerformanceDetail && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSelectedPerformanceDetail(null);
            }
          }}
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 transition-all duration-300 cursor-pointer"
        >
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] flex flex-col shadow-2xl overflow-hidden border border-slate-100 transform scale-100 transition-all duration-300 cursor-default">
            {/* Header */}
            <div className="bg-slate-50 px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-base font-extrabold text-slate-900 tracking-tight">
                  {selectedPerformanceDetail.employeeName}
                </h3>
                <span className="block text-[11px] text-slate-600 font-semibold uppercase mt-0.5 tracking-wider">
                  {selectedPerformanceDetail.role} ({selectedPerformanceDetail.department})
                </span>
              </div>
              <button
                onClick={() => setSelectedPerformanceDetail(null)}
                className="h-8 w-8 rounded-full bg-slate-100 hover:bg-slate-200/80 text-slate-550 hover:text-slate-700 flex items-center justify-center transition-all cursor-pointer border border-transparent hover:border-slate-200"
              >
                <FiX size={16} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {/* Summary Banner */}
              {(selectedPerformanceDetail.type === "late" || selectedPerformanceDetail.type === "early") && (
                <div className="bg-gradient-to-r from-indigo-50/50 to-indigo-100/30 p-4 rounded-2xl border border-indigo-100 flex justify-between items-center shadow-xs">
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">
                      {selectedPerformanceDetail.type === "late" ? "Total Late Time" : "Total Early Out Time"}
                    </span>
                    <span className="text-lg font-black text-rose-650 mt-1 font-mono">
                      {selectedPerformanceDetail.type === "late"
                        ? formatDeductionTime(selectedPerformanceDetail.monthlyStats?.lateMinutes || 0)
                        : formatDeductionTime(selectedPerformanceDetail.monthlyStats?.earlyOutMinutes || 0)}
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">
                      Total Days
                    </span>
                    <span className="text-lg font-black text-slate-800 mt-1">
                      {selectedPerformanceDetail.type === "late"
                        ? selectedPerformanceDetail.monthlyStats?.lateCount
                        : selectedPerformanceDetail.monthlyStats?.earlyCount} Days
                    </span>
                  </div>
                </div>
              )}

              {selectedPerformanceDetail.type === "absent" && (
                <div className="bg-gradient-to-r from-rose-50/50 to-rose-100/30 p-4 rounded-2xl border border-rose-100 flex justify-between items-center shadow-xs">
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">
                      Total Absences
                    </span>
                    <span className="text-lg font-black text-rose-700 mt-1">
                      {selectedPerformanceDetail.monthlyStats?.absentDays} Days
                    </span>
                  </div>
                </div>
              )}

              <div className="flex justify-between items-center bg-slate-50 p-3.5 rounded-xl border border-slate-100">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                  Record Type:
                </span>
                <span className={`text-xs font-extrabold px-3 py-1 rounded-full border uppercase tracking-wider ${selectedPerformanceDetail.type === "late"
                  ? "bg-amber-50 text-amber-700 border-amber-100"
                  : selectedPerformanceDetail.type === "early"
                    ? "bg-rose-50 text-rose-700 border-rose-100"
                    : "bg-indigo-50 text-indigo-700 border-indigo-100"
                  }`}>
                  {selectedPerformanceDetail.type === "late"
                    ? "Late Arrivals"
                    : selectedPerformanceDetail.type === "early"
                      ? "Early Departures"
                      : "Absences"}
                </span>
              </div>

              {/* Timeline Items */}
              <div className="space-y-3">
                {selectedPerformanceDetail.records.length > 0 ? (
                  selectedPerformanceDetail.records.map((rec, index) => {
                    const lateMins = selectedPerformanceDetail.type === "late"
                      ? getDiffMinutes(rec.inTime, selectedPerformanceDetail.checkInTime || "10:00")
                      : 0;
                    const earlyMins = selectedPerformanceDetail.type === "early"
                      ? getDiffMinutes(selectedPerformanceDetail.checkOutTime || "19:00", rec.outTime)
                      : 0;

                    return (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3.5 bg-slate-50/30 rounded-xl border border-slate-200/50 hover:bg-slate-50 transition-all hover:translate-x-0.5 shadow-xs"
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-lg bg-white border border-slate-200/60 flex items-center justify-center text-slate-400 shadow-xs">
                            <FiCalendar size={14} />
                          </div>
                          <div>
                            <span className="font-bold text-slate-800 text-xs sm:text-sm">
                              {formatDateDMY(rec.date)}
                            </span>
                            <span className="block text-[10px] text-slate-400 mt-0.5 font-medium">
                              {selectedPerformanceDetail.type === "late"
                                ? `Punch In Time: ${rec.inTime || "--:--"}`
                                : selectedPerformanceDetail.type === "early"
                                  ? `Punch Out Time: ${rec.outTime || "--:--"}`
                                  : "No punch record"}
                            </span>
                          </div>
                        </div>

                        <div>
                          {selectedPerformanceDetail.type === "late" ? (
                            <span className="text-xs font-black text-rose-600 bg-rose-50 px-2.5 py-1 rounded-lg border border-rose-100 font-mono">
                              +{lateMins} mins
                            </span>
                          ) : selectedPerformanceDetail.type === "early" ? (
                            <span className="text-xs font-black text-rose-600 bg-rose-50 px-2.5 py-1 rounded-lg border border-rose-100 font-mono">
                              {earlyMins} mins early
                            </span>
                          ) : (
                            <span className="text-xs font-extrabold text-rose-600 bg-rose-50 px-2.5 py-1 rounded-lg border border-rose-100">
                              Absent
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center text-slate-400 py-12">
                    <FiUserCheck className="mx-auto text-emerald-500 mb-3" size={36} />
                    <p className="font-bold text-slate-600 text-sm">Perfect Performance</p>
                    <p className="text-xs text-slate-450 mt-1">This employee has no records of this type in this range.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="bg-slate-50 px-6 py-4 border-t border-slate-100 flex justify-end flex-shrink-0">
              <button
                onClick={() => setSelectedPerformanceDetail(null)}
                className="px-4 py-2 bg-white border border-slate-200 text-slate-600 hover:text-slate-800 hover:bg-slate-50 text-xs font-bold rounded-xl transition-all shadow-sm cursor-pointer"
              >
                Close Details
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceAnalyticsDashboard;
